import { lazyModule } from "../lib/lazyModule";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import MapGL, { Source, Layer, Marker, Popup } from "react-map-gl/mapbox";  // aliased: don't shadow the built-in Map (used for cannibalisation)
import "mapbox-gl/dist/mapbox-gl.css";
import {
  X, Upload, Settings, Plus, MapPin, Loader2, Trash2, Store as StoreIcon,
  Building2, Radar, ChevronDown, FileText, Sparkles, Download,
  TrendingUp, TrendingDown, Minus, AlertTriangle, Users,
  ZoomIn, ZoomOut, Crosshair, Eye, Layers, Star, ArrowUpDown, ExternalLink, Share2 } from "lucide-react";
import ShareModal from "../components/ShareModal";
import { MAPBOX_TOKEN, MAP_STYLES, styleUrl, parseCoordinateInput } from "@landhep/map-engine";
import { postDemandGrid, postDeepAnalysisV2, postProScoredGrids } from "@landhep/api-client";
import {
  brListCities, brListStores, brCreateStore, brCreateStoresBulk, brDeleteStore,
  brListClasses, brSaveClass, brPointScore, brGetStoreDeepAnalysis, brSaveStoreDeepAnalysis,
  brListStoreDAVersions, brGetStoreDAVersion,
  brRunMonitor, brListMonitorRuns, brListCompetitors, brPreviewCompetitors,
  brAddCompetitor, brDeleteCompetitor,
  brGetSubscription, brSubscribe, brCancelSubscription, brSetSchedule, brUpdateStore,
  getBillingStatus, getOwnedCities,
} from "../lib/api";
import {
  DeepAnalysisHero, DeepAnalysisReport, exportDeepAnalysisPDF, deepAnalysisTicket, deriveStoreDeepAnalysisView,
  buildProfileForFit,
} from "./DeepAnalysis";

// Company onboarding profile (what they sell) — every store is analyzed as this business.
function loadCompanyProfile() {
  try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}"); }
  catch { return {}; }
}
// Use the SHARED whitelist so Radar's deep analysis carries exactly what the live
// page sends (Trade Areas fields included) — one source of truth, no drift.
const profileForFit = (p) => buildProfileForFit(p);

// ── Constants / helpers ──────────────────────────────────────────────────────
const EMPTY_GRID = { type: "FeatureCollection", features: [] };
const DEFAULT_RADIUS_KM = 2.0;

const DEMAND_LAYERS = [
  { id: "primary",    color: "#34a853", opacity: 0.34 },
  { id: "untapped",   color: "#a855f7", opacity: 0.34 },
  { id: "split",      color: "#ea4335", opacity: 0.34 },
  { id: "competitor", color: "#fbbc04", opacity: 0.34 },
];

// Store lifecycle — drives the marker colour and whether the store feeds the
// demand grid. Plans (construction / planning) are scenario-only by default.
const STATUS_META = {
  running:      { label: "Berjalan",           color: "#14543e", counts: true  },
  construction: { label: "Dalam pembangunan", color: "#f59e0b", counts: false },
  planning:     { label: "Perencanaan",          color: "#8b5cf6", counts: false },
  inactive:     { label: "Non-aktif",        color: "#ef4444", counts: false },
};
const STATUS_KEYS = Object.keys(STATUS_META);
const statusColor = (s) => STATUS_META[s]?.color ?? "#64748b";
const DRAFT_ID = "__draft__";

// Distinct accent per class label (stable by hashing the label).
const CLASS_COLORS = ["#14543e", "#ea4335", "#34a853", "#a855f7", "#fb923c", "#0891b2", "#db2777", "#65a30d"];
function classColor(label) {
  if (!label) return "#64748b";
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return CLASS_COLORS[h % CLASS_COLORS.length];
}

// Distinct colour per ecosystem-POI category (stable by hashing the label).
const POI_COLORS = ["#6366f1", "#0891b2", "#d97706", "#7c3aed", "#0d9488", "#c026d3", "#2563eb", "#65a30d", "#e11d48"];
function poiColor(cat) {
  if (!cat) return "#6366f1";
  let h = 0;
  for (let i = 0; i < cat.length; i++) h = (h * 31 + cat.charCodeAt(i)) >>> 0;
  return POI_COLORS[h % POI_COLORS.length];
}

// Cells are identical across grid runs (same bounds + step) → key by first vertex.
const cellKey = (f) => {
  const c = f?.geometry?.coordinates?.[0]?.[0];
  return c ? `${c[0].toFixed(6)},${c[1].toFixed(6)}` : "";
};

// Great-circle distance in metres (for competitor distance from a store).
function haversineM(aLat, aLng, bLat, bLng) {
  if ([aLat, aLng, bLat, bLng].some((v) => v == null)) return null;
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}
const fmtDistance = (m) => (m == null ? "—" : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`);

function circlePolygon(lat, lng, radiusKm, pts = 64) {
  const coords = Array.from({ length: pts + 1 }, (_, i) => {
    const a = (i / pts) * 2 * Math.PI;
    return [lng + (radiusKm / (111 * Math.cos((lat * Math.PI) / 180))) * Math.cos(a), lat + (radiusKm / 111) * Math.sin(a)];
  });
  return { type: "Feature", geometry: { type: "Polygon", coordinates: [coords] } };
}

// Bounding box around a set of stores, with padding (for grid + map fit).
function storesBounds(stores, padDeg = 0.03) {
  if (!stores.length) return null;
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  for (const s of stores) {
    minLat = Math.min(minLat, s.lat); maxLat = Math.max(maxLat, s.lat);
    minLng = Math.min(minLng, s.lng); maxLng = Math.max(maxLng, s.lng);
  }
  return {
    minLat: minLat - padDeg, maxLat: maxLat + padDeg,
    minLng: minLng - padDeg, maxLng: maxLng + padDeg,
  };
}

const STORE_STATUSES = ["running", "construction", "planning", "inactive"];

// Tolerant CSV parser. Required (positional or header): name, class, latitude,
// longitude. Optional, HEADER-ONLY so they never shift the required columns:
// status, notes, google_maps_url, performa. A blank/unknown status becomes
// "planning" (an unverified imported store).
function parseStoreCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], errors: ["File kosong"] };

  const splitLine = (l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const first = splitLine(lines[0]).map((c) => c.toLowerCase());
  const headerHints = ["name", "nama", "class", "kelas", "lat", "latitude", "lng", "lon", "long", "longitude",
    "status", "notes", "note", "catatan", "google_maps_url", "google maps", "maps", "gmaps", "performa"];
  const hasHeader = first.some((c) => headerHints.includes(c));

  const idx = { name: 0, cls: 1, lat: 2, lng: 3, status: -1, notes: -1, gmaps: -1, performa: -1 };
  if (hasHeader) {
    first.forEach((c, i) => {
      if (["name", "nama", "store", "toko"].includes(c)) idx.name = i;
      else if (["class", "kelas", "tier"].includes(c)) idx.cls = i;
      else if (["lat", "latitude"].includes(c)) idx.lat = i;
      else if (["lng", "lon", "long", "longitude"].includes(c)) idx.lng = i;
      else if (c === "status") idx.status = i;
      else if (["notes", "note", "catatan"].includes(c)) idx.notes = i;
      else if (["google_maps_url", "google maps", "google map link", "maps", "gmaps", "url", "link"].includes(c)) idx.gmaps = i;
      else if (["performa", "performance", "perf"].includes(c)) idx.performa = i;
    });
  }
  const at = (c, i) => (i >= 0 ? (c[i] || "") : "");

  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows = [], errors = [];
  dataLines.forEach((l, i) => {
    const c = splitLine(l);
    const name = c[idx.name];
    const lat = parseFloat(c[idx.lat]);
    const lng = parseFloat(c[idx.lng]);
    if (!name || Number.isNaN(lat) || Number.isNaN(lng)) {
      errors.push(`Baris ${i + 1}: nama, lat, atau lng tidak ada/tidak valid`);
      return;
    }
    const st = at(c, idx.status).toLowerCase();
    rows.push({
      name, storeClass: at(c, idx.cls), lat, lng,
      status: STORE_STATUSES.includes(st) ? st : "planning",
      notes: at(c, idx.notes) || undefined,
      googleMapsUrl: at(c, idx.gmaps) || undefined,
      performa: at(c, idx.performa) || undefined,
    });
  });
  return { rows, errors };
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function BranchRadar() {
  const [cities, setCities]       = useState([]);
  const [city, setCity]           = useState(null);   // kodeKabkot or "all"
  const [stores, setStores]       = useState([]);
  const [classes, setClasses]     = useState([]);
  const [loading, setLoading]     = useState(true);

  const [grid, setGrid]           = useState(EMPTY_GRID);           // with the draft, if any
  const [baselineGrid, setBaselineGrid] = useState(EMPTY_GRID);     // without the draft
  const [gridLoading, setGridLoading] = useState(false);

  const [selected, setSelected]   = useState(null);   // selected store id
  const [addMode, setAddMode]     = useState(false);
  // Draft store — lives only on the map until the user saves it.
  const [draft, setDraft]         = useState(null);   // {lat,lng,name,storeClass,status,competitors}
  const [statusFilter, setStatusFilter] = useState(STATUS_KEYS);    // which statuses to show
  const [includePlans, setIncludePlans] = useState(false);          // count plans in demand?
  const [showUpload, setShowUpload] = useState(false);
  const [showClasses, setShowClasses] = useState(false);
  const [showTable, setShowTable] = useState(false);   // tabular dashboard
  const [allStores, setAllStores] = useState(null);    // ALL stores (every city) for the tabular view
  const [editStore, setEditStore] = useState(null);    // store being edited in a popup

  const [view, setView] = useState({ longitude: 106.82, latitude: -6.20, zoom: 10.5 });

  // Map controls (mirrors the Analysis page): basemap, opacity, coordinate locator.
  const mapRef = useRef(null);
  const [mapStyle, setMapStyle]           = useState("streets-v12");
  const [gridOpacity, setGridOpacity]     = useState(0.5);
  const [showOpacity, setShowOpacity]     = useState(false);
  const [coordInput, setCoordInput]       = useState("");
  const [coordError, setCoordError]       = useState("");
  const [coordPin, setCoordPin]           = useState(null);   // black reference dot from the locator

  function handleCoordSubmit(e) {
    e.preventDefault();
    const parsed = parseCoordinateInput(coordInput);
    if (!parsed) { setCoordError("Tidak terbaca — tempel koordinat atau URL Google Maps."); return; }
    setCoordError("");
    setCoordPin({ lat: parsed.lat, lng: parsed.lng });   // drop a visual reference marker
    mapRef.current?.flyTo({ center: [parsed.lng, parsed.lat], zoom: 15, duration: 900 });
  }

  // Per-store deep analysis
  const [daCache, setDaCache]   = useState({});   // storeId → { result, savedAt, siteScore, subcategory, targetSes }
  const [generating, setGenerating] = useState(null); // storeId being generated
  const [genError, setGenError] = useState(null);
  const [report, setReport]     = useState(null); // { store, result, score, subcategory, targetSes }

  // Credit-safety: every action that spends credits / a paid API call is gated by a
  // confirmation, then a full-page blocking overlay during processing (so the page
  // cannot be mis-clicked), mirroring the main Deep Analysis page.
  const [confirmAsk, setConfirmAsk] = useState(null);  // { title, body, confirmLabel, label, run }
  const [processing, setProcessing] = useState(null);  // label shown in the blocking overlay
  const requestConfirm = (opts) => setConfirmAsk(opts);
  async function runConfirmed() {
    const c = confirmAsk; setConfirmAsk(null);
    if (!c?.run) return;
    setProcessing(c.label || "Memproses…");
    try { await c.run(); } finally { setProcessing(null); }
  }

  const RADIUS = "3km";

  // Load any persisted deep-analysis when a store is selected.
  useEffect(() => {
    if (!selected) return;
    if (daCache[selected] !== undefined) return;
    brGetStoreDeepAnalysis(selected)
      .then((cache) => {
        const entry = Object.values(cache || {})[0]; // one report per store (MVP)
        setDaCache((m) => ({ ...m, [selected]: entry?.payload ?? entry ?? null }));
      })
      .catch(() => setDaCache((m) => ({ ...m, [selected]: null })));
  }, [selected]); // eslint-disable-line

  async function generateForStore(store) {
    setGenerating(store.id); setGenError(null);
    try {
      const profile = loadCompanyProfile();
      const subcategory = profile?.bizSubtype ?? null;
      const targetSes   = profile?.target_ses ?? "all";
      const fit = profileForFit(profile);

      // 1) Real site score for this exact point.
      const ps = await brPointScore({ lat: store.lat, lng: store.lng, subcategory, target_ses: targetSes, radius: RADIUS, profile: fit });
      const score = Math.floor(ps?.site_score ?? 0);

      // 2) On-ground deep analysis at the store point (Gemini + Google Maps).
      const result = await postDeepAnalysisV2({
        grid_id: ps?.grid_id ?? null, lat: store.lat, lng: store.lng, score,
        subtype: subcategory, target_ses: targetSes, radius: RADIUS,
        pop_radius: profile?.populationCatchment ?? "5km",
        benchmarks: profile?.benchmarks ?? [], area: store.namaKabkot,
        kode_kelurahan: ps?.kode_kelurahan ?? store.kodeKelurahan, resolution: "100m",
        profile: fit,
        location_info: { kelurahan: store.namaKelurahan, kecamatan: store.namaKecamatan, kabkot: store.namaKabkot, provinsi: store.namaProvinsi },
      });
      if (!result?.ok) throw new Error(result?.error || "Analisis gagal");

      // 3) Persist (charges the per-report price). gridId ties this to the grid so
      //    it becomes an OWNED grid (never charged twice for the same location).
      const savedAt = Date.now();
      const payload = { result, savedAt, siteScore: score, subcategory, targetSes };
      await brSaveStoreDeepAnalysis(store.id, {
        cacheKey: `v1__${RADIUS}__${subcategory || "general"}__${targetSes}`,
        // radius drives the price — a wider scan costs more in Maps/Places calls.
        payload, meta: { subcategory, target_ses: targetSes, resolution: "100m", radius: RADIUS, gridId: ps?.grid_id ?? null },
      });

      setDaCache((m) => ({ ...m, [store.id]: payload }));
      setReport({ store, result, score, subcategory, targetSes });
    } catch (e) {
      setGenError(e.message);
    } finally {
      setGenerating(null);
    }
  }

  function viewStoreReport(store) {
    const p = daCache[store.id];
    if (!p?.result) return;
    setReport({ store, result: p.result, score: p.siteScore ?? 0, subcategory: p.subcategory ?? null, targetSes: p.targetSes ?? "all" });
  }

  // ── Competitor markers ────────────────────────────────────────────────────────
  const [competitors, setCompetitors] = useState({}); // storeId → competitor[]

  const loadCompetitors = useCallback(async (storeId) => {
    try {
      const list = await brListCompetitors(storeId);
      setCompetitors((m) => ({ ...m, [storeId]: list }));
    } catch { setCompetitors((m) => ({ ...m, [storeId]: [] })); }
  }, []);

  useEffect(() => { if (selected) loadCompetitors(selected); }, [selected, loadCompetitors]);

  const selectedCompetitors = competitors[selected] || [];

  // ── Map layers, competitor interactivity (Phase C) ───────────────────────────
  const [layers, setLayers] = useState({ demand: true, competitors: true, score: false, poi: false });
  const [showAllComp, setShowAllComp] = useState(false);   // competitors for ALL stores, not just selected
  const [compDetail, setCompDetail] = useState(null);       // clicked competitor → map popup
  const [poiDetail, setPoiDetail] = useState(null);         // clicked ecosystem POI → map popup
  const [compSort, setCompSort] = useState("distance");     // "distance" | "rating"
  const [scoreGrid, setScoreGrid] = useState(EMPTY_GRID);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreLocked, setScoreLocked] = useState(null);   // city name when site score is locked (unowned)
  // Demand grid and Site score are mutually exclusive (never stacked); competitors
  // toggles independently.
  const toggleLayer = (k) => setLayers((l) => {
    const next = { ...l, [k]: !l[k] };
    if (k === "score" && next.score) next.demand = false;
    if (k === "demand" && next.demand) next.score = false;
    return next;
  });

  // ── Monitor: backend crawls Google Places + recomputes demand → diff vs last run ─
  const [monitoring, setMonitoring] = useState(null);
  const [monitorResult, setMonitorResult] = useState(null); // { store, res, runs }
  const [monitorReport, setMonitorReport] = useState(null); // { store, runs } — full report
  const [reportBusy, setReportBusy] = useState(false);

  async function monitorStore(store) {
    setMonitoring(store.id); setGenError(null);
    try {
      const subcategory = loadCompanyProfile()?.bizSubtype ?? null;
      const res  = await brRunMonitor(store.id, { trigger: "manual", subcategory });
      const runs = await brListMonitorRuns(store.id);
      await loadCompetitors(store.id);
      setMonitorResult({ store, res, runs });
    } catch (e) {
      setGenError(e.message);
    } finally {
      setMonitoring(null);
    }
  }

  async function openMonitorReport(store) {
    setReportBusy(true); setGenError(null);
    try {
      const runs = await brListMonitorRuns(store.id);
      setMonitorReport({ store, runs });
    } catch (e) {
      setGenError(e.message);
    } finally {
      setReportBusy(false);
    }
  }

  // ── Subscription (Phase 4) + schedule (Phase 5) ───────────────────────────────
  const [subs, setSubs]     = useState({});   // storeId → subscription
  const [subBusy, setSubBusy] = useState(false);

  useEffect(() => {
    if (!selected || subs[selected] !== undefined) return;
    brGetSubscription(selected)
      .then((s) => setSubs((m) => ({ ...m, [selected]: s })))
      .catch(() => setSubs((m) => ({ ...m, [selected]: { status: "none" } })));
  }, [selected]); // eslint-disable-line

  async function doSubscribe(store) {
    setSubBusy(true); setGenError(null);
    try {
      const { subscription } = await brSubscribe(store.id);
      setSubs((m) => ({ ...m, [store.id]: { ...subscription, configuredPrice: subs[store.id]?.configuredPrice } }));
    } catch (e) { setGenError(e.message); }
    finally { setSubBusy(false); }
  }
  async function doCancel(store) {
    setSubBusy(true);
    try { await brCancelSubscription(store.id); setSubs((m) => ({ ...m, [store.id]: { ...m[store.id], status: "cancelled", monitorFrequency: "off" } })); }
    finally { setSubBusy(false); }
  }
  async function doSchedule(store, frequency) {
    setSubBusy(true);
    try {
      const r = await brSetSchedule(store.id, frequency);
      setSubs((m) => ({ ...m, [store.id]: { ...m[store.id], monitorFrequency: r.monitorFrequency, nextRunAt: r.nextRunAt } }));
    } finally { setSubBusy(false); }
  }

  const radiusForClass = useCallback((label) => {
    const c = classes.find((x) => x.classLabel === label);
    return c?.radiusKm ?? DEFAULT_RADIUS_KM;
  }, [classes]);

  // Initial load: cities + classes.
  useEffect(() => {
    (async () => {
      try {
        const [cs, cls] = await Promise.all([brListCities(), brListClasses()]);
        setCities(cs);
        setClasses(cls);
        setCity(cs.length ? cs[0].kodeKabkot : "all");
      } catch { /* not authed / empty */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Load stores whenever the city changes.
  const reloadStores = useCallback(async (targetCity) => {
    const c = targetCity ?? city;
    if (c == null) return;
    const list = await brListStores(c === "all" ? undefined : c);
    setStores(list);
    const b = storesBounds(list);
    if (b) setView((v) => ({ ...v, longitude: (b.minLng + b.maxLng) / 2, latitude: (b.minLat + b.maxLat) / 2 }));
  }, [city]);

  useEffect(() => { if (city != null) reloadStores(city); }, [city, reloadStores]);

  // Stores visible on the map (status filter).
  const visibleStores = useMemo(
    () => stores.filter((s) => statusFilter.includes(s.status || "running")),
    [stores, statusFilter]
  );

  // Stores that actually generate demand: running always; plans only when the user
  // opts in ("Include plans" = see the world as if my pipeline were open).
  const demandStores = useMemo(() => stores.filter((s) => {
    const st = s.status || "running";
    return STATUS_META[st]?.counts || (includePlans && (st === "construction" || st === "planning"));
  }), [stores, includePlans]);

  // Demand grid. When a draft pin exists we fetch TWO grids — with and without it —
  // so the map shows the candidate's effect and we can measure cannibalisation.
  useEffect(() => {
    const base = demandStores.map((s) => ({ id: s.id, lat: s.lat, lng: s.lng, radius_km: radiusForClass(s.storeClass) }));
    // A specific city always shows its whole-city grid (even with no stores yet, so
    // the untapped population is visible). "All cities" falls back to the stores' area.
    const citySpecific = city && city !== "all";
    if (!base.length && !draft && !citySpecific) { setGrid(EMPTY_GRID); setBaselineGrid(EMPTY_GRID); return; }

    const pts = draft ? [...demandStores, draft] : demandStores;
    const common = citySpecific
      ? { kode_kabkot: city, competitors: [] }
      : { bounds: storesBounds(pts, 0.02), competitors: [] };
    const withDraft = draft
      ? [...base, { id: DRAFT_ID, lat: draft.lat, lng: draft.lng, radius_km: radiusForClass(draft.storeClass) }]
      : base;

    let cancelled = false;
    setGridLoading(true);
    Promise.all([
      postDemandGrid({ ...common, active_stores: withDraft }),
      draft ? postDemandGrid({ ...common, active_stores: base }) : Promise.resolve(null),
    ])
      .then(([g, bg]) => {
        if (cancelled) return;
        const main = g?.features ? g : EMPTY_GRID;
        setGrid(main);
        setBaselineGrid(bg?.features ? bg : main);
      })
      .catch(() => { if (!cancelled) { setGrid(EMPTY_GRID); setBaselineGrid(EMPTY_GRID); } })
      .finally(() => { if (!cancelled) setGridLoading(false); });
    return () => { cancelled = true; };
  }, [demandStores, draft?.lat, draft?.lng, city]); // eslint-disable-line

  // How much of the draft's catchment is genuinely new vs stolen from our own stores.
  const cannibal = useMemo(() => {
    if (!draft || !grid.features?.length || !baselineGrid.features?.length) return null;
    const before = new Map(baselineGrid.features.map((f) => [cellKey(f), f.properties]));
    let captured = 0, fromOwn = 0, fromUntapped = 0;
    const affected = new Set();
    for (const f of grid.features) {
      const p = f.properties;
      if (String(p.store_id) !== DRAFT_ID) continue;
      if (p.type !== "primary" && p.type !== "split") continue;
      captured++;
      const b = before.get(cellKey(f));
      if (!b) continue;
      if (b.type === "primary" || b.type === "split") { fromOwn++; if (b.store_id) affected.add(String(b.store_id)); }
      else if (b.type === "untapped") fromUntapped++;
    }
    const names = [...affected]
      .map((id) => stores.find((s) => s.id === id)?.name)
      .filter(Boolean);
    return { captured, fromOwn, fromUntapped, affected: names };
  }, [grid, baselineGrid, draft, stores]);

  const serviceRings = useMemo(() => ({
    type: "FeatureCollection",
    features: [
      ...visibleStores.map((s) => circlePolygon(s.lat, s.lng, radiusForClass(s.storeClass))),
      ...(draft ? [circlePolygon(draft.lat, draft.lng, radiusForClass(draft.storeClass))] : []),
    ],
  }), [visibleStores, draft, radiusForClass]);

  const selectedStore = stores.find((s) => s.id === selected) || null;

  // "Show all competitors" — pull competitors for every visible store on demand.
  useEffect(() => {
    if (!showAllComp) return;
    const missing = visibleStores.filter((s) => competitors[s.id] === undefined);
    if (!missing.length) return;
    Promise.all(missing.map((s) => brListCompetitors(s.id).then((l) => [s.id, l]).catch(() => [s.id, []])))
      .then((pairs) => setCompetitors((m) => ({ ...m, ...Object.fromEntries(pairs) })));
  }, [showAllComp, visibleStores]); // eslint-disable-line

  // Competitors currently drawn: all visible stores' (tagged with their store) or just the selected store's.
  const drawnCompetitors = useMemo(() => {
    if (draft?.competitors) return draft.competitors.map((c) => ({ ...c, _storeId: DRAFT_ID }));
    if (showAllComp) return visibleStores.flatMap((s) => (competitors[s.id] || []).map((c) => ({ ...c, _storeId: s.id, _storeLat: s.lat, _storeLng: s.lng, _storeName: s.name })));
    if (selected) return selectedCompetitors.map((c) => ({ ...c, _storeId: selected, _storeLat: selectedStore?.lat, _storeLng: selectedStore?.lng, _storeName: selectedStore?.name }));
    return [];
  }, [draft, showAllComp, visibleStores, competitors, selected, selectedCompetitors, selectedStore]);

  // Selected store's competitors with distance, sorted for the list panel.
  const rankedCompetitors = useMemo(() => {
    if (!selectedStore) return [];
    const withDist = selectedCompetitors.map((c) => ({ ...c, _dist: haversineM(selectedStore.lat, selectedStore.lng, c.lat, c.lng) }));
    withDist.sort((a, b) => compSort === "rating"
      ? (b.rating ?? -1) - (a.rating ?? -1)
      : (a._dist ?? Infinity) - (b._dist ?? Infinity));
    return withDist;
  }, [selectedCompetitors, selectedStore, compSort]);

  // Ecosystem POIs from the selected store's deep analysis (banks, transit, health,
  // F&B, etc. — each carries lat/lng). Plotted when the POI layer is on and the
  // store has a deep analysis. Deduped by name+coord across categories.
  const daForSelected = selected ? (daCache[selected]?.result?.analysis ?? null) : null;
  const poiFeatures = useMemo(() => {
    const eco = daForSelected?.ecosystem;
    if (!Array.isArray(eco)) return [];
    const seen = new Set();
    const out = [];
    for (const cat of eco) {
      for (const p of (cat.places || [])) {
        if (p.lat == null || p.lng == null) continue;
        const key = `${p.name}|${p.lat.toFixed(5)}|${p.lng.toFixed(5)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ ...p, category: cat.category });
      }
    }
    return out;
  }, [daForSelected]);

  // POI category filter (chips). poiCatsOff = categories hidden by the user.
  const [poiCatsOff, setPoiCatsOff] = useState(() => new Set());
  const togglePoiCat = (c) => setPoiCatsOff((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const poiCategories = useMemo(() => {
    const m = new Map();
    for (const p of poiFeatures) m.set(p.category, (m.get(p.category) || 0) + 1);
    return [...m.entries()].map(([category, count]) => ({ category, count })).sort((a, b) => b.count - a.count);
  }, [poiFeatures]);
  const visiblePoi = useMemo(() => poiFeatures.filter((p) => !poiCatsOff.has(p.category)), [poiFeatures, poiCatsOff]);

  // Site-score overlay for the selected city (owned — the user has stores there).
  // Sends the SAME payload the Analysis page does (full profile incl. the affinity
  // layer, profile resolution, 3km catchment) so the two maps score identically.
  const scoreResolution = loadCompanyProfile()?.gridResolution === "1km" ? "1km" : "100m";
  useEffect(() => {
    const citySpecific = city && city !== "all";
    // Drop the previous city's score grid immediately so stale cells never linger
    // on the map (and don't weigh it down) while the new city loads.
    setScoreGrid(EMPTY_GRID);
    setScoreLocked(null);
    if (!layers.score || !citySpecific) { setScoreLoading(false); return; }
    const profile = loadCompanyProfile();
    let cancelled = false;
    setScoreLoading(true);
    (async () => {
      // Ownership gate: site score is a paid, city-owned feature. If billing is on
      // and this city isn't owned, don't fetch — show an info alert and stop.
      try {
        const billing = await getBillingStatus();
        if (billing?.enabled && profile?.id) {
          const owned = await getOwnedCities(profile.id);
          const isOwned = (owned || []).some((c) => c.kode_kabkot === city && c.is_active);
          if (!isOwned) {
            if (!cancelled) {
              setScoreLocked(cities.find((c) => c.kodeKabkot === city)?.namaKabkot || "kota ini");
              setScoreGrid(EMPTY_GRID);
              setScoreLoading(false);
            }
            return;
          }
        }
      } catch { /* if the ownership check itself fails, fall through and try to load */ }
      try {
        const d = await postProScoredGrids(city, {
          subcategory: profile?.bizSubtype ?? "", target_ses: profile?.target_ses ?? "all",
          resolution: scoreResolution, radius: profile?.populationCatchment ?? "5km", profile: profileForFit(profile),
        });
        if (!cancelled) setScoreGrid(d?.features ? d : EMPTY_GRID);
      } catch { if (!cancelled) setScoreGrid(EMPTY_GRID); }
      finally { if (!cancelled) setScoreLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [layers.score, city]); // eslint-disable-line

  // Per-city quintile cut points for the Site-score overlay — colour is relative
  // to the selected city (same as the Analysis page), so every city shows its best
  // areas green and its weakest red. The score number stays absolute.
  const scoreCuts = useMemo(() => {
    const s = (scoreGrid.features || [])
      .filter((f) => !f.properties.grid_filtered && f.properties.grid_score != null)
      .map((f) => f.properties.grid_score)
      .sort((a, b) => a - b);
    if (s.length < 6) return [20, 40, 60, 80, 90];
    const q = (p) => s[Math.min(s.length - 1, Math.floor((s.length - 1) * p))];
    const cuts = [q(0.2), q(0.4), q(0.6), q(0.8), q(0.95)];   // Best = exclusive top ~5%
    for (let i = 1; i < cuts.length; i++) if (cuts[i] <= cuts[i - 1]) cuts[i] = cuts[i - 1] + 1e-6;
    return cuts;
  }, [scoreGrid]);

  // Manually adding a competitor to the selected store by clicking the map.
  const [compAddMode, setCompAddMode] = useState(false);
  const [pendingComp, setPendingComp] = useState(null); // {lat,lng}

  function handleMapClick(e) {
    if (compAddMode && selected) {
      setPendingComp({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      setCompAddMode(false);
      return;
    }
    if (!addMode) { setSelected(null); return; }
    // Drop a DRAFT pin — nothing is written to the database yet. The demand grid
    // re-renders around it so the user can see cannibalisation before committing.
    setDraft({
      lat: e.lngLat.lat, lng: e.lngLat.lng,
      name: "", storeClass: "", status: "planning", competitors: null,
    });
    setSelected(null);
    setAddMode(false);
  }

  async function saveManualCompetitor(name) {
    if (!selected || !pendingComp) return;
    await brAddCompetitor(selected, { name, lat: pendingComp.lat, lng: pendingComp.lng });
    setPendingComp(null);
    await loadCompetitors(selected);
  }
  async function removeCompetitor(c) {
    if (!selected) return;
    await brDeleteCompetitor(selected, c.id);
    await loadCompetitors(selected);
  }

  async function refreshAll() {
    const cs = await brListCities();
    setCities(cs);
    setClasses(await brListClasses());
    await reloadStores();
  }

  // Move a store through its lifecycle (planning → construction → running …).
  const [statusBusy, setStatusBusy] = useState(false);
  async function changeStatus(store, status) {
    if (status === (store.status || "running")) return;
    setStatusBusy(true);
    try {
      const updated = await brUpdateStore(store.id, { status });
      setStores((list) => list.map((s) => (s.id === store.id ? { ...s, status: updated.status } : s)));
    } catch (e) { setGenError(e.message); }
    finally { setStatusBusy(false); }
  }

  // Tabular view shows EVERY store (all cities), independent of the map's city
  // filter — so "All cities" in the table really means all, even when the map is
  // zoomed to one city. Fetched fresh each open.
  async function openTable() {
    setShowTable(true);
    try { setAllStores(await brListStores()); }
    catch { setAllStores(null); }
  }

  // Edit store fields from the tabular popup — update both the (map-scoped) list
  // and the full all-cities list so the table reflects the change immediately.
  async function saveStoreEdit(id, patch) {
    const updated = await brUpdateStore(id, patch);
    const apply = (list) => list.map((s) => (s.id === id ? { ...s, ...updated } : s));
    setStores(apply);
    setAllStores((list) => (list ? apply(list) : list));
    return updated;
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="relative w-full h-screen overflow-hidden bg-slate-100">
      {/* Map */}
      <div className="absolute inset-0">
        <MapGL ref={mapRef} {...view} onMove={(e) => setView(e.viewState)} onClick={handleMapClick}
          style={{ width: "100%", height: "100%" }} mapStyle={styleUrl(mapStyle)}
          mapboxAccessToken={MAPBOX_TOKEN} cursor={(addMode || compAddMode) ? "crosshair" : "grab"}>

          {/* 100m site-score overlay (owned city) — drawn UNDER the demand grid. */}
          {layers.score && (
            <Source id="br-score" type="geojson" data={scoreGrid}>
              {/* Same per-city relative palette as the Analysis page: 6 tiers by
                  rank within this city (red = weakest … deep green "Best" = top ~5%).
                  The score number itself stays absolute. */}
              <Layer id="br-score-fill" type="fill" source="br-score"
                filter={["!", ["get", "grid_filtered"]]}
                paint={{ "fill-color": ["case", ["==", ["typeof", ["get", "grid_score"]], "number"],
                  ["step", ["get", "grid_score"], "#ef4444", scoreCuts[0], "#f97316", scoreCuts[1], "#eab308", scoreCuts[2], "#84cc16", scoreCuts[3], "#4ade80", scoreCuts[4], "#15803d"],
                  "#cbd5e1"],
                  "fill-opacity": gridOpacity }} />
            </Source>
          )}

          {layers.demand && (
            <Source id="br-grid" type="geojson" data={grid}>
              {DEMAND_LAYERS.map(({ id, color }) => (
                <Layer key={id} id={`br-grid-${id}`} type="fill" source="br-grid"
                  filter={["==", ["get", "type"], id]}
                  paint={{ "fill-color": color, "fill-opacity": ["*", ["coalesce", ["get", "w"], 0.6], 0.72 * gridOpacity] }} />
              ))}
              <Layer id="br-grid-border" type="line" source="br-grid"
                paint={{ "line-color": "#ffffff", "line-width": 0.3, "line-opacity": 0.4 }} />
            </Source>
          )}

          <Source id="br-rings" type="geojson" data={serviceRings}>
            <Layer id="br-rings-line" type="line"
              paint={{ "line-color": "#14543e", "line-width": 1.2, "line-dasharray": [3, 2], "line-opacity": 0.5 }} />
          </Source>

          {/* Stores — colour = lifecycle status */}
          {visibleStores.map((s) => (
            <Marker key={s.id} longitude={s.lng} latitude={s.lat} anchor="center"
              onClick={(e) => { e.originalEvent.stopPropagation(); setSelected(s.id); }}>
              <div className="group relative flex flex-col items-center cursor-pointer" title={s.name}>
                <div className={`w-3.5 h-3.5 rounded-full border-2 border-white shadow ${selected === s.id ? "ring-2 ring-offset-1 ring-slate-700" : ""}`}
                  style={{ backgroundColor: statusColor(s.status) }} />
                <div className="pointer-events-none absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900/90 px-2 py-1 text-[10px] font-semibold text-white">
                  {s.name} · {STATUS_META[s.status || "running"]?.label}
                </div>
              </div>
            </Marker>
          ))}

          {/* Competitors — clickable. Selected store's (or all stores' when "show all"),
              or the draft preview. Manual ones (source=manual) render as a hollow diamond. */}
          {layers.competitors && drawnCompetitors
            .filter((c) => c.lat != null && c.lng != null)
            .map((c, i) => {
              const manual = c.source === "manual";
              const active = compDetail && (compDetail.id ?? compDetail.place_id) === (c.id ?? c.place_id);
              return (
                <Marker key={`${c._storeId}-${c.id ?? c.place_id ?? i}`} longitude={c.lng} latitude={c.lat} anchor="center"
                  onClick={(e) => { e.originalEvent.stopPropagation(); setCompDetail(c); }}>
                  <div className="group relative flex flex-col items-center cursor-pointer">
                    <div className={`rotate-45 border shadow transition-all ${active ? "w-4 h-4 ring-2 ring-red-300" : "w-3 h-3"} ${manual ? "bg-white border-red-500" : "bg-red-500 border-white"}`} />
                    <div className="pointer-events-none absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900/90 px-2 py-1 text-[10px] font-semibold text-white">
                      {c.name}{c.rating ? ` · ${c.rating}★` : manual ? " · ditambahkan oleh Anda" : ""}
                    </div>
                  </div>
                </Marker>
              );
            })}

          {/* Competitor detail popup */}
          {compDetail && compDetail.lat != null && compDetail.lng != null && (
            <Popup longitude={compDetail.lng} latitude={compDetail.lat} anchor="bottom" offset={14}
              closeOnClick={false} onClose={() => setCompDetail(null)} maxWidth="260px">
              <div className="p-1 min-w-[190px]">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-extrabold text-slate-800 leading-tight">{compDetail.name || "Kompetitor"}</p>
                  {compDetail.source === "manual" && <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 rounded px-1 py-0.5">Manual</span>}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
                  {compDetail.rating != null && <span className="inline-flex items-center gap-1 font-bold text-amber-600"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{compDetail.rating.toFixed(1)}</span>}
                  {compDetail.userRatingsTotal != null && <span className="text-slate-400">{compDetail.userRatingsTotal} ulasan</span>}
                </div>
                {compDetail._storeLat != null && (
                  <p className="mt-1 text-[10px] text-slate-500">{fmtDistance(haversineM(compDetail._storeLat, compDetail._storeLng, compDetail.lat, compDetail.lng))} dari {compDetail._storeName || "toko"}</p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <a href={compDetail.placeId ? `https://www.google.com/maps/place/?q=place_id:${compDetail.placeId}` : `https://www.google.com/maps/search/?api=1&query=${compDetail.lat},${compDetail.lng}`}
                    target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-600 hover:text-brand-700">
                    <ExternalLink className="w-3 h-3" /> Google Maps
                  </a>
                  {compDetail.source === "manual" && compDetail.id && !draft && (
                    <button onClick={() => { removeCompetitor(compDetail); setCompDetail(null); }}
                      className="inline-flex items-center gap-1 text-[10px] font-bold text-red-500 hover:text-red-600"><Trash2 className="w-3 h-3" /> Hapus</button>
                  )}
                </div>
              </div>
            </Popup>
          )}

          {/* Ecosystem POIs from the selected store's deep analysis (clickable dots) */}
          {layers.poi && visiblePoi.map((p, i) => {
            const active = poiDetail && poiDetail._i === i;
            return (
              <Marker key={`poi-${i}`} longitude={p.lng} latitude={p.lat} anchor="center"
                onClick={(e) => { e.originalEvent.stopPropagation(); setPoiDetail({ ...p, _i: i }); }}>
                <div className="group relative flex items-center justify-center cursor-pointer">
                  <div className={`rounded-full border border-white shadow transition-all ${active ? "w-3.5 h-3.5 ring-2 ring-offset-1" : "w-2.5 h-2.5"}`}
                    style={{ backgroundColor: poiColor(p.category), ...(active ? { "--tw-ring-color": poiColor(p.category) } : {}) }} />
                  <div className="pointer-events-none absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900/90 px-2 py-1 text-[10px] font-semibold text-white z-10">
                    {p.name} · {p.category}{p.distance_label ? ` · ${p.distance_label}` : ""}
                  </div>
                </div>
              </Marker>
            );
          })}

          {/* POI detail popup */}
          {poiDetail && poiDetail.lat != null && (
            <Popup longitude={poiDetail.lng} latitude={poiDetail.lat} anchor="bottom" offset={12}
              closeOnClick={false} onClose={() => setPoiDetail(null)} maxWidth="240px">
              <div className="p-1 min-w-[180px]">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: poiColor(poiDetail.category) }} />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{poiDetail.category}</span>
                </div>
                <p className="text-xs font-extrabold text-slate-800 leading-tight mt-1">{poiDetail.name || "POI"}</p>
                {poiDetail.type_label && <p className="text-[10px] text-slate-500 mt-0.5">{poiDetail.type_label}</p>}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
                  {poiDetail.rating != null && <span className="inline-flex items-center gap-1 font-bold text-amber-600"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{poiDetail.rating}</span>}
                  {poiDetail.distance_label && <span className="text-slate-400">berjarak {poiDetail.distance_label}</span>}
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${poiDetail.lat},${poiDetail.lng}`}
                  target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-brand-600 hover:text-brand-700">
                  <ExternalLink className="w-3 h-3" /> Google Maps
                </a>
              </div>
            </Popup>
          )}

          {/* Coordinate/Google-Maps reference dot (black) — visual only, a pin to aim
              at when adding a store or competitor. */}
          {coordPin && (
            <Marker longitude={coordPin.lng} latitude={coordPin.lat} anchor="center"
              onClick={(e) => { e.originalEvent.stopPropagation(); setCoordPin(null); }}>
              <div className="group relative flex flex-col items-center cursor-pointer" title="Titik referensi — klik untuk menghapus">
                <div className="w-3 h-3 rounded-full bg-black border-2 border-white shadow-md" />
                <div className="pointer-events-none absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900/90 px-2 py-1 text-[10px] font-semibold text-white">
                  Referensi · {coordPin.lat.toFixed(5)}, {coordPin.lng.toFixed(5)}
                </div>
              </div>
            </Marker>
          )}

          {/* Pending manual-competitor pin (before naming) */}
          {pendingComp && (
            <Marker longitude={pendingComp.lng} latitude={pendingComp.lat} anchor="center">
              <div className="w-3.5 h-3.5 rotate-45 bg-white border-2 border-red-600 shadow animate-pulse" />
            </Marker>
          )}

          {/* Draft store — draggable; the grid recomputes as you move it */}
          {draft && (
            <Marker longitude={draft.lng} latitude={draft.lat} anchor="bottom" draggable
              onDragEnd={(e) => setDraft((d) => ({ ...d, lat: e.lngLat.lat, lng: e.lngLat.lng }))}>
              <div className="flex flex-col items-center">
                <MapPin className="w-7 h-7 drop-shadow" style={{ color: statusColor(draft.status) }} fill={statusColor(draft.status)} />
                <span className="mt-0.5 rounded bg-slate-900/85 px-1.5 py-0.5 text-[9px] font-bold text-white">DRAF · seret saya</span>
              </div>
            </Marker>
          )}
        </MapGL>
      </div>

      {/* Header (offset to clear the floating Navbar + Sidebar of MapLayout) */}
      <div className="absolute top-[84px] left-[100px] right-3 z-20 flex items-start justify-between gap-3 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2 bg-white/95 backdrop-blur rounded-2xl shadow-sm border border-slate-100 px-3 py-2">
          <Radar className="w-5 h-5 text-brand-600" />
          <div>
            <p className="text-sm font-extrabold text-slate-800 leading-none">Branch Radar</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Pantau permintaan & kompetitor di seluruh toko Anda</p>
          </div>
        </div>

        <div className="pointer-events-auto flex items-center gap-2">
          <CityPicker cities={cities} value={city} onChange={(c) => { setCity(c); setSelected(null); }}
            totalStores={cities.reduce((s, c) => s + (c.storeCount || 0), 0)} />
          <button onClick={openTable}
            className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
            <StoreIcon className="w-3.5 h-3.5" /> Tabular
          </button>
          <button onClick={() => setShowClasses(true)}
            className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
            <Settings className="w-3.5 h-3.5" /> Kelas
          </button>
          <button onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
            <Upload className="w-3.5 h-3.5" /> Unggah CSV
          </button>
          <button onClick={() => { setAddMode((m) => !m); setDraft(null); }}
            className={`flex items-center gap-1.5 rounded-xl shadow-sm border px-3 py-2 text-xs font-bold transition-colors ${addMode ? "bg-brand-700 text-white border-brand-700" : "bg-white/95 text-slate-600 border-slate-100 hover:bg-slate-50"}`}>
            <Plus className="w-3.5 h-3.5" /> {addMode ? "Klik peta…" : "Tambah Toko"}
          </button>
        </div>
      </div>

      {/* Status filter + "include plans" — one tidy bar, aligned under the title card */}
      <div className="absolute top-[140px] left-[100px] z-20 pointer-events-auto flex flex-wrap items-center gap-1 w-[300px] bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-1.5 py-1.5">
        {STATUS_KEYS.map((k) => {
          const on = statusFilter.includes(k);
          const n = stores.filter((s) => (s.status || "running") === k).length;
          return (
            <button key={k} title={STATUS_META[k].label}
              onClick={() => setStatusFilter((f) => f.includes(k) ? f.filter((x) => x !== k) : [...f, k])}
              className={`flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-bold transition-opacity ${on ? "opacity-100" : "opacity-35"}`}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_META[k].color }} />
              <span className="text-slate-600">{STATUS_META[k].label}</span>
              <span className="text-slate-400">{n}</span>
            </button>
          );
        })}
        <button onClick={() => setIncludePlans((v) => !v)}
          title="Hitung toko dalam pembangunan & perencanaan seolah sudah beroperasi"
          className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-bold transition-colors ${includePlans ? "bg-violet-600 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
          <span className={`w-2 h-2 rounded-full ${includePlans ? "bg-white" : "bg-slate-300"}`} />
          Ikutkan rencana dalam permintaan
        </button>
      </div>

      {/* Left: store list */}
      <div className="absolute top-[206px] left-[100px] bottom-3 z-10 w-[300px] flex flex-col bg-white/95 backdrop-blur rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Toko Saya</span>
          <span className="text-[10px] font-bold text-brand-600">{visibleStores.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" /> Memuat…</div>
          ) : stores.length === 0 ? (
            <EmptyStores onUpload={() => setShowUpload(true)} />
          ) : (
            visibleStores.map((s) => (
              <StoreRow key={s.id} store={s} active={selected === s.id}
                radiusKm={radiusForClass(s.storeClass)}
                onClick={() => { setSelected(s.id); setView((v) => ({ ...v, longitude: s.lng, latitude: s.lat })); }} />
            ))
          )}
        </div>
      </div>

      {/* Coordinate / Google Maps URL locator (top-centre, clear of the header) */}
      <div className="absolute top-[84px] left-1/2 -translate-x-1/2 z-20 pointer-events-auto w-[300px] bg-white rounded-2xl p-2 shadow-md">
        <form onSubmit={handleCoordSubmit} className="flex items-center gap-1.5">
          <Crosshair className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-1" />
          <input value={coordInput}
            onChange={(e) => { setCoordInput(e.target.value); if (coordError) setCoordError(""); }}
            placeholder="Koordinat atau URL Google Maps"
            className="flex-1 min-w-0 text-[11px] text-gray-700 placeholder-gray-400 bg-transparent outline-none py-1" />
          <button type="submit" disabled={!coordInput.trim()}
            className="shrink-0 px-2.5 py-1 rounded-lg bg-brand-600 text-white text-[11px] font-semibold hover:bg-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Buka
          </button>
        </form>
        {coordError && <p className="text-[10px] text-red-500 leading-snug px-1 pt-1.5">{coordError}</p>}
      </div>

      {/* Map controls (top-right): zoom · opacity · basemap. Hidden while a store panel
          is open (they share the top-right corner) — deselect to bring them back. */}
      {!selectedStore && (
      <div className="absolute top-[140px] right-3 z-20 flex flex-col gap-2 items-end pointer-events-none">
        <div className="pointer-events-auto bg-white rounded-2xl p-1.5 flex flex-col gap-0.5 shadow-md">
          <button onClick={() => mapRef.current?.flyTo({ zoom: mapRef.current.getZoom() + 1, duration: 300 })}
            className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors">
            <ZoomIn className="w-3.5 h-3.5 text-gray-500" /><span className="text-xs font-semibold text-gray-500">Perbesar</span>
          </button>
          <button onClick={() => mapRef.current?.flyTo({ zoom: mapRef.current.getZoom() - 1, duration: 300 })}
            className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors">
            <ZoomOut className="w-3.5 h-3.5 text-gray-500" /><span className="text-xs font-semibold text-gray-500">Perkecil</span>
          </button>
          <div className="h-px bg-gray-100 mx-2" />
          <div className="relative">
            <button onClick={() => setShowOpacity((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors w-full">
              <Eye className="w-3.5 h-3.5 text-gray-500" /><span className="text-xs font-semibold text-gray-500">Opasitas</span>
            </button>
            {showOpacity && (
              <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-3 flex flex-col gap-1 min-w-[180px]">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{layers.score ? "Skor Lokasi" : "Grid Permintaan"}</span>
                <div className="flex items-center gap-2">
                  <input type="range" min={0.1} max={1} step={0.05} value={gridOpacity}
                    onChange={(e) => setGridOpacity(Number(e.target.value))}
                    className="flex-1 h-1 cursor-pointer accent-brand-600" />
                  <span className="text-[10px] font-mono text-slate-500 w-7 text-right">{Math.round(gridOpacity * 100)}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="pointer-events-auto bg-white rounded-2xl p-2.5 shadow-md flex flex-col gap-1">
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5 px-1">Peta dasar</p>
          {MAP_STYLES.map((style) => {
            const active = mapStyle === style.id;
            return (
              <button key={style.id} onClick={() => setMapStyle(style.id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-xl text-left transition-colors ${active ? "bg-[#e3eae8]" : "hover:bg-gray-50"}`}>
                <span className="w-4 h-4 rounded-md shrink-0 border border-black/10" style={{ backgroundColor: style.color }} />
                <span className={`text-[11px] font-semibold whitespace-nowrap ${active ? "text-[#14543e]" : "text-gray-500"}`}>{style.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Layers control (bottom-left, clear of the store list) — declutter the map
          and, for a selected city, overlay the 100m site-score heatmap. */}
      <div className="absolute bottom-3 left-[416px] z-20 pointer-events-auto bg-white/95 backdrop-blur rounded-2xl shadow-md border border-slate-100 px-3 py-2.5">
        <div className="flex items-center gap-1.5 mb-2">
          <Layers className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Lapisan</span>
        </div>
        <div className="flex flex-col gap-1">
          <LayerToggle on={layers.demand} onClick={() => toggleLayer("demand")} color="#34a853" label="Grid Permintaan" />
          <LayerToggle on={layers.competitors} onClick={() => toggleLayer("competitors")} color="#ea4335" label="Kompetitor" diamond />
          <LayerToggle on={layers.score} onClick={() => toggleLayer("score")}
            color="#16a34a" label={`Skor Lokasi (${scoreResolution})`}
            hint={!city || city === "all" ? "pilih kota" : scoreLoading ? "memuat…" : undefined}
            disabled={!city || city === "all"} />
          <LayerToggle on={layers.poi} onClick={() => toggleLayer("poi")}
            color="#6366f1" label="POI (analisis mendalam)"
            hint={!selected ? "pilih toko" : !daForSelected ? "belum ada analisis mendalam" : poiFeatures.length ? `${poiFeatures.length}` : "tidak ada"}
            disabled={!selected || !daForSelected || poiFeatures.length === 0} />
        </div>

        {/* POI category filter — toggle categories on/off */}
        {layers.poi && poiCategories.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Kategori POI</span>
              {poiCatsOff.size > 0 && (
                <button onClick={() => setPoiCatsOff(new Set())} className="text-[9px] font-bold text-brand-600 hover:text-brand-700">Semua</button>
              )}
            </div>
            <div className="flex flex-wrap gap-1 max-w-[220px]">
              {poiCategories.map(({ category, count }) => {
                const off = poiCatsOff.has(category);
                return (
                  <button key={category} onClick={() => togglePoiCat(category)}
                    className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-bold transition-opacity ${off ? "opacity-35 border-slate-200" : "border-slate-300"}`}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: poiColor(category) }} />
                    <span className="text-slate-600">{category}</span>
                    <span className="text-slate-400">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-2 pt-2 border-t border-slate-100">
          <button onClick={() => setShowAllComp((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1 w-full text-[10px] font-bold transition-colors ${showAllComp ? "bg-brand-700 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
            <Users className="w-3 h-3" /> {showAllComp ? "Menampilkan kompetitor semua toko" : "Tampilkan semua kompetitor"}
          </button>
        </div>
      </div>

      {/* Right: selected store panel */}
      {selectedStore && (
        <StorePanel store={selectedStore} radiusKm={radiusForClass(selectedStore.storeClass)}
          competitors={rankedCompetitors} compSort={compSort} onCompSort={setCompSort}
          onCompFocus={(c) => { setCompDetail(c); mapRef.current?.flyTo({ center: [c.lng, c.lat], zoom: 16, duration: 700 }); }}
          hasReport={!!daCache[selectedStore.id]?.result}
          generating={generating === selectedStore.id}
          monitoring={monitoring === selectedStore.id}
          subscription={subs[selectedStore.id]}
          subBusy={subBusy}
          statusBusy={statusBusy}
          competitorCount={selectedCompetitors.length}
          compAddMode={compAddMode}
          error={genError}
          onGenerate={() => requestConfirm({
            title: "Buat Analisis Mendalam?",
            body: `Laporan analisis mendalam lengkap akan dibuat untuk "${selectedStore.name}". Ini akan membebankan biaya ke akun Anda untuk laporan berbayar (skor lokasi + kompetitor + pasar + data properti).`,
            confirmLabel: "Buat laporan", label: "Memindai lokasi — membuat laporan…",
            run: () => generateForStore(selectedStore),
          })}
          onView={() => viewStoreReport(selectedStore)}
          onMonitor={() => requestConfirm({
            title: "Pantau sekarang?",
            body: `Ini memindai kompetitor di sekitar "${selectedStore.name}" melalui Google Places dan mencatat snapshot pemantauan. Ini akan menghitung ke batas pemantauan harian Anda.`,
            confirmLabel: "Jalankan pemantauan", label: "Memindai kompetitor…",
            run: () => monitorStore(selectedStore),
          })}
          onReport={() => openMonitorReport(selectedStore)}
          reportBusy={reportBusy}
          onStatus={(s) => changeStatus(selectedStore, s)}
          onAddCompetitor={() => setCompAddMode((m) => !m)}
          onSubscribe={() => requestConfirm({
            // A paid action: the old button charged the month on the first click.
            title: "Berlangganan pemantauan?",
            body: `Pemantauan otomatis untuk "${selectedStore.name}" ditagih ${rupiah(subs[selectedStore.id]?.configuredPrice ?? 0)} per bulan dari kredit Anda, mulai sekarang. Bisa dibatalkan kapan saja dari panel toko ini.`,
            confirmLabel: `Langganan · ${rupiah(subs[selectedStore.id]?.configuredPrice ?? 0)}/bln`, label: "Mengaktifkan langganan…",
            run: () => doSubscribe(selectedStore),
          })}
          onCancelSub={() => doCancel(selectedStore)}
          onSchedule={(f) => doSchedule(selectedStore, f)}
          onClose={() => { setSelected(null); setCompAddMode(false); }}
          onDelete={async () => { await brDeleteStore(selectedStore.id); setSelected(null); await refreshAll(); }} />
      )}

      {/* Name a manually-added competitor */}
      {pendingComp && (
        <NameCompetitorModal at={pendingComp} onClose={() => setPendingComp(null)} onSave={saveManualCompetitor} />
      )}

      {/* Deep analysis report modal (reuses the live Deep Analysis stack) */}
      {report && <StoreReportModal {...report} onClose={() => setReport(null)} />}

      {/* Monitor result modal (immediate, post-run) */}
      {monitorResult && (
        <MonitorResultModal {...monitorResult}
          onClose={() => setMonitorResult(null)}
          onViewReport={() => { const s = monitorResult.store, r = monitorResult.runs; setMonitorResult(null); setMonitorReport({ store: s, runs: r }); }} />
      )}

      {/* Monitor Report modal (full, separate from deep analysis) */}
      {monitorReport && <MonitorReportModal {...monitorReport} onClose={() => setMonitorReport(null)} />}

      {/* Tabular view + edit popup — shows ALL stores (every city), independent of
          the map's city filter; defaults to "All cities". */}
      {showTable && (
        <StoreTableModal stores={allStores ?? stores} cities={cities} city="all" classes={classes}
          competitors={competitors}
          onClose={() => setShowTable(false)}
          onEdit={(s) => setEditStore(s)}
          onLocate={(s) => { setShowTable(false); setCity(s.kodeKabkot ?? "all"); setSelected(s.id); mapRef.current?.flyTo({ center: [s.lng, s.lat], zoom: 15, duration: 800 }); }} />
      )}
      {editStore && (
        <StoreEditModal store={editStore} classes={classes}
          onClose={() => setEditStore(null)}
          onSave={async (patch) => { const u = await saveStoreEdit(editStore.id, patch); setEditStore(null); return u; }} />
      )}

      {/* Credit-safety: confirm before spending, block the page while processing */}
      {confirmAsk && <ConfirmDialog {...confirmAsk} onConfirm={runConfirmed} onCancel={() => setConfirmAsk(null)} />}
      {processing && <ProcessingOverlay label={processing} />}

      {/* Site-score loading — a light, mostly-transparent screen cover so the map
          reads as "busy" without a hard block. */}
      {scoreLoading && (
        <div className="fixed inset-0 z-[45] flex items-center justify-center bg-white/30 backdrop-blur-[1px] cursor-wait">
          <div className="flex items-center gap-2 rounded-xl bg-white/95 shadow-lg border border-slate-100 px-4 py-2.5">
            <Loader2 className="w-4 h-4 animate-spin text-brand-600" />
            <span className="text-sm font-semibold text-slate-600">Memuat skor lokasi…</span>
          </div>
        </div>
      )}

      {/* Site score locked — the city isn't owned. */}
      {scoreLocked && (
        <div className="absolute top-[140px] left-1/2 -translate-x-1/2 z-30 pointer-events-auto max-w-md">
          <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 shadow-md px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-800 leading-relaxed">
              <b>Skor lokasi terkunci untuk {scoreLocked}.</b> Ini adalah lapisan berbayar berbasis kepemilikan kota — buka kota ini (Analisis → buka kunci) untuk melihat skor lokasinya. Kompetitor dan grid permintaan tetap berfungsi.
            </div>
            <button onClick={() => { setScoreLocked(null); toggleLayer("score"); }} className="text-amber-400 hover:text-amber-600 shrink-0"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Legend */}
      {stores.length > 0 && <Legend loading={gridLoading} />}

      {/* Draft store panel — the map stays visible so cannibalisation is readable */}
      {draft && (
        <DraftPanel draft={draft} setDraft={setDraft} classes={classes}
          radiusKm={radiusForClass(draft.storeClass)}
          cannibal={cannibal} gridLoading={gridLoading}
          requestConfirm={requestConfirm}
          onDiscard={() => setDraft(null)}
          onSaved={async () => { setDraft(null); await refreshAll(); }} />
      )}
      {showUpload && (
        // Refresh on close too: closing the "2 toko diimpor" sheet by clicking
        // outside left the list saying "Belum ada toko" until a reload.
        <UploadModal onClose={async () => { setShowUpload(false); await refreshAll(); }}
          onDone={async () => { setShowUpload(false); await refreshAll(); }} />
      )}
      {showClasses && (
        <ClassSettingsModal classes={classes} onClose={() => setShowClasses(false)}
          onSaved={async () => { setClasses(await brListClasses()); }} />
      )}
    </div>
  );
}

// ── City picker ──────────────────────────────────────────────────────────────
function CityPicker({ cities, value, onChange, totalStores }) {
  const [open, setOpen] = useState(false);
  const current = value === "all" ? "Semua kota" : (cities.find((c) => c.kodeKabkot === value)?.namaKabkot || "Pilih kota");
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 min-w-[150px]">
        <Building2 className="w-3.5 h-3.5 text-slate-400" />
        <span className="truncate flex-1 text-left">{current}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 w-64 bg-white rounded-xl shadow-lg border border-slate-100 py-1 max-h-72 overflow-y-auto z-30">
          <button onClick={() => { onChange("all"); setOpen(false); }}
            className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50 text-left">
            <span className="font-semibold text-slate-700">Semua kota</span>
            <span className="text-slate-400">{totalStores}</span>
          </button>
          {cities.map((c) => (
            <button key={c.kodeKabkot} onClick={() => { onChange(c.kodeKabkot); setOpen(false); }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-slate-50 text-left">
              <span className="truncate text-slate-700">{c.namaKabkot}</span>
              <span className="text-slate-400 shrink-0">{c.storeCount}</span>
            </button>
          ))}
          {cities.length === 0 && <p className="px-3 py-2 text-[11px] text-slate-400">Belum ada toko — unggah CSV.</p>}
        </div>
      )}
    </div>
  );
}

function StoreRow({ store, active, radiusKm, onClick }) {
  const st = store.status || "running";
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 hover:bg-slate-50 transition-colors ${active ? "bg-teal-50 border-l-2 border-l-brand-600" : ""}`}>
      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: statusColor(st) }} title={STATUS_META[st]?.label} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-slate-800 truncate">{store.name}</p>
        <p className="text-[10px] text-slate-400 truncate">
          {[STATUS_META[st]?.label, store.storeClass || "belum dikelaskan", store.namaKecamatan].filter(Boolean).join(" · ")}
        </p>
      </div>
      <span className="text-[9px] font-mono text-slate-400 shrink-0">{radiusKm.toFixed(1)}km</span>
    </button>
  );
}

// ── Draft store: simulate on the map before it ever touches the database ─────────
function DraftPanel({ draft, setDraft, classes, radiusKm, cannibal, gridLoading, requestConfirm, onDiscard, onSaved }) {
  const [busy, setBusy]     = useState(false);
  const [scanning, setScan] = useState(false);
  const [err, setErr]       = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const askScan = () => requestConfirm({
    title: "Pindai kompetitor di sini?",
    body: "Ini mencari kompetitor di sekitar titik ini melalui Google Places — satu panggilan berbayar.",
    confirmLabel: "Pindai", label: "Memindai kompetitor…",
    run: scanCompetitors,
  });

  async function scanCompetitors() {
    setScan(true); setErr(null);
    try {
      const businessType = loadCompanyProfile()?.bizSubtype || null;
      const { competitors } = await brPreviewCompetitors({
        lat: draft.lat, lng: draft.lng, radiusKm, businessType,
      });
      setDraft((d) => ({ ...d, competitors: competitors || [] }));
    } catch (e) { setErr(e.message); }
    finally { setScan(false); }
  }

  async function save() {
    if (!draft.name.trim()) { setErr("Nama toko wajib diisi"); return; }
    setBusy(true); setErr(null);
    try {
      await brCreateStore({
        name: draft.name.trim(), storeClass: (draft.storeClass || "").trim(),
        status: draft.status, lat: draft.lat, lng: draft.lng, source: "manual",
        businessType: loadCompanyProfile()?.bizSubtype || null,
        // Reuse anything already scanned in the preview — don't pay Places twice.
        competitors: draft.competitors || undefined,
        crawlCompetitors: !draft.competitors,
      });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="absolute top-[186px] right-3 z-30 w-[320px] max-h-[calc(100%-198px)] flex flex-col bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
      <div className="shrink-0 px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-sm font-extrabold text-slate-800">Toko baru — draf</p>
          <p className="text-[10px] text-slate-400">Belum disimpan · seret pin untuk mencoba lokasi</p>
        </div>
        <button onClick={onDiscard} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>

      {/* Header and actions stay put; everything between scrolls, so the panel
          never runs past a short screen and "Simpan toko" is always reachable. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {/* Cannibalisation read-out — the whole point of the preview */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Dampak Permintaan</span>
          {gridLoading && <Loader2 className="w-3 h-3 text-slate-300 animate-spin" />}
        </div>
        {!cannibal ? (
          <p className="text-[11px] text-slate-400">Menghitung…</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Metric label="Menangkap" value={cannibal.captured} sub="sel" />
              <Metric label="Permintaan baru" value={cannibal.fromUntapped} sub="belum tergarap" accent="#16a34a" />
              <Metric label="Kanibalisasi" value={cannibal.fromOwn} sub="dari milik sendiri"
                accent={cannibal.fromOwn > 0 ? "#ea4335" : undefined} />
            </div>
            {cannibal.fromOwn > 0 && (
              <p className="mt-2 text-[10px] text-red-500 leading-relaxed">
                Tumpang tindih dengan <b>{cannibal.affected.join(", ") || "toko Anda"}</b> — sel merah di peta kini diperebutkan.
              </p>
            )}
            {cannibal.fromOwn === 0 && cannibal.captured > 0 && (
              <p className="mt-2 text-[10px] text-green-600">Tidak ada tumpang tindih dengan toko Anda yang sudah ada.</p>
            )}
          </>
        )}
      </div>

      <div className="px-4 py-3 space-y-3">
        <Field label="Nama toko">
          <input autoFocus value={draft.name} onChange={(e) => set("name", e.target.value)}
            className="br-input" placeholder="misalnya Cabang Menteng" />
        </Field>
        <Field label="Kelas (menentukan radius)">
          <input value={draft.storeClass} onChange={(e) => set("storeClass", e.target.value)}
            list="br-class-list" className="br-input" placeholder="misalnya flagship" />
          <datalist id="br-class-list">{classes.map((c) => <option key={c.classLabel} value={c.classLabel} />)}</datalist>
        </Field>
        <div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Status</span>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {STATUS_KEYS.map((k) => (
              <button key={k} onClick={() => set("status", k)}
                className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] font-bold transition-colors ${draft.status === k ? "border-slate-800 bg-slate-50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_META[k].color }} />
                {STATUS_META[k].label}
              </button>
            ))}
          </div>
        </div>

        <button onClick={askScan} disabled={scanning}
          className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-[11px] font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
          {scanning ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memindai Google Places…</>
            : draft.competitors ? <><Users className="w-3.5 h-3.5" /> {draft.competitors.length} kompetitor ditemukan · pindai ulang</>
            : <><Users className="w-3.5 h-3.5" /> Pindai kompetitor di sini</>}
        </button>

        <p className="text-[10px] text-slate-400 font-mono">{draft.lat.toFixed(5)}, {draft.lng.toFixed(5)}</p>
        {err && <p className="text-[11px] text-red-500">{err}</p>}
      </div>

      </div>

      <div className="shrink-0 px-4 py-3 border-t border-slate-100 flex gap-2">
        <button onClick={onDiscard}
          className="flex-1 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50">Batalkan</button>
        <button onClick={save} disabled={busy}
          className="flex-1 py-2.5 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800 disabled:opacity-50">
          {busy ? "Menyimpan…" : "Simpan toko"}
        </button>
      </div>
    </div>
  );
}

const rupiah = (n) => "Rp " + Number(n ?? 0).toLocaleString("id-ID");

function NameCompetitorModal({ at, onClose, onSave }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function save() {
    if (!name.trim()) { setErr("Nama wajib diisi"); return; }
    setBusy(true); setErr(null);
    try { await onSave(name.trim()); }
    catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title="Tambah kompetitor" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Nama kompetitor">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()} className="br-input" placeholder="misalnya Kopi Sebelah" />
        </Field>
        <p className="text-[11px] text-slate-400">Ditandai di {at.lat.toFixed(5)}, {at.lng.toFixed(5)}.</p>
        {err && <p className="text-[11px] text-red-500">{err}</p>}
      </div>
      <ModalActions onCancel={onClose} onConfirm={save} busy={busy} confirmLabel="Tambah kompetitor" />
    </Modal>
  );
}

function StorePanel({ store, radiusKm, hasReport, generating, monitoring, subscription, subBusy,
                     statusBusy, competitorCount, competitors = [], compSort, onCompSort, onCompFocus,
                     compAddMode, error, onGenerate, onView, onMonitor,
                     onReport, reportBusy, onStatus, onAddCompetitor, onSubscribe, onCancelSub, onSchedule, onClose, onDelete }) {
  const subActive = subscription?.status === "active";
  return (
    <div className="absolute top-[140px] right-3 bottom-3 z-20 w-[300px] max-h-[calc(100vh-156px)] flex flex-col bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-slate-800 truncate">{store.name}</p>
          <p className="text-[10px] text-slate-400 truncate">{[store.namaKelurahan, store.namaKabkot].filter(Boolean).join(", ")}</p>
          <a href={store.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`}
            target="_blank" rel="noreferrer"
            className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold text-brand-600 hover:text-brand-700">
            <MapPin className="w-3 h-3" /> {store.googleMapsUrl ? "Buka di Google Maps" : "Lihat koordinat di Peta"}
          </a>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 shrink-0"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto">
      <div className="px-4 py-3 space-y-2">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</span>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {STATUS_KEYS.map((k) => (
              <button key={k} onClick={() => onStatus(k)} disabled={statusBusy}
                className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] font-bold transition-colors disabled:opacity-50 ${(store.status || "running") === k ? "border-slate-800 bg-slate-50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_META[k].color }} />
                {STATUS_META[k].label}
              </button>
            ))}
          </div>
        </div>
        <Row label="Kelas"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: classColor(store.storeClass) }} />{store.storeClass || "belum dikelaskan"}</span></Row>
        <Row label="Radius Layanan">{radiusKm.toFixed(2)} km</Row>
        <Row label="Koordinat">{store.lat.toFixed(5)}, {store.lng.toFixed(5)}</Row>
        {store.externalRef && <Row label="Referensi">{store.externalRef}</Row>}
      </div>
      <div className="px-4 pb-4 space-y-2">
        {hasReport ? (
          <>
            <button onClick={onView}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-700 px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-800">
              <FileText className="w-3.5 h-3.5" /> Lihat Analisis Mendalam
            </button>
            <button onClick={onGenerate} disabled={generating}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-50 disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" /> {generating ? "Membuat ulang…" : "Buat Ulang"}
            </button>
          </>
        ) : (
          <button onClick={onGenerate} disabled={generating}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-700 px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-800 disabled:opacity-60">
            {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memindai lokasi…</> : <><Sparkles className="w-3.5 h-3.5" /> Buat Analisis Mendalam</>}
          </button>
        )}
        {/* Monitoring — gated by a per-store subscription */}
        {subActive ? (
          <>
            <button onClick={onMonitor} disabled={monitoring}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-brand-200 bg-teal-50/60 px-4 py-2.5 text-xs font-bold text-brand-700 hover:bg-teal-50 disabled:opacity-60">
              {monitoring ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memindai kompetitor…</> : <><Radar className="w-3.5 h-3.5" /> Pantau Sekarang</>}
            </button>
            <button onClick={onReport} disabled={reportBusy}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-50 disabled:opacity-50">
              {reportBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat…</> : <><TrendingUp className="w-3.5 h-3.5" /> Laporan Pemantauan</>}
            </button>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-600">Pemantauan aktif</span>
                <button onClick={onCancelSub} disabled={subBusy} className="text-[10px] font-bold text-red-400 hover:text-red-600">Batal</button>
              </div>
              <p className="text-[10px] text-slate-400 mb-1.5">Jadwal pemindaian otomatis</p>
              <div className="flex gap-1">
                {["off", "weekly", "monthly"].map((f) => (
                  <button key={f} onClick={() => onSchedule(f)} disabled={subBusy}
                    className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold capitalize transition-colors ${subscription?.monitorFrequency === f ? "bg-brand-700 text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-brand-300"}`}>
                    {f}
                  </button>
                ))}
              </div>
              {subscription?.nextRunAt && subscription?.monitorFrequency !== "off" && (
                <p className="text-[9px] text-slate-400 mt-1.5">Pemindaian otomatis berikutnya: {new Date(subscription.nextRunAt).toLocaleDateString("id-ID", { day: "2-digit", month: "short" })}</p>
              )}
            </div>
          </>
        ) : (
          <button onClick={onSubscribe} disabled={subBusy}
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-brand-200 bg-teal-50/60 px-4 py-2.5 text-xs font-bold text-brand-700 hover:bg-teal-50 disabled:opacity-60">
            <Radar className="w-3.5 h-3.5" />
            {subBusy ? "Berlangganan…" : `Berlangganan Pantau · ${rupiah(subscription?.configuredPrice ?? 0)}/bln`}
          </button>
        )}

        {/* Competitors: count + manual add + sortable list */}
        <div className="rounded-xl border border-slate-100 bg-slate-50/60">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
              <span className="w-2 h-2 rotate-45 bg-red-500" /> {competitorCount ?? 0} kompetitor
            </span>
            <button onClick={onAddCompetitor}
              className={`text-[10px] font-bold transition-colors ${compAddMode ? "text-brand-700" : "text-slate-400 hover:text-brand-600"}`}>
              {compAddMode ? "Klik peta…" : "+ Tambah di peta"}
            </button>
          </div>
          {competitors.length > 0 && (
            <>
              <div className="flex items-center gap-1 px-3 pb-1.5">
                <ArrowUpDown className="w-3 h-3 text-slate-300" />
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mr-1">Urutkan</span>
                {[["distance", "Jarak"], ["rating", "Rating"]].map(([k, l]) => (
                  <button key={k} onClick={() => onCompSort?.(k)}
                    className={`text-[9px] font-bold rounded px-1.5 py-0.5 ${compSort === k ? "bg-slate-700 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{l}</button>
                ))}
              </div>
              <div className="max-h-40 overflow-y-auto border-t border-slate-100">
                {competitors.map((c, i) => (
                  <button key={c.id ?? c.place_id ?? i} onClick={() => onCompFocus?.(c)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-white transition-colors border-b border-slate-50 last:border-0">
                    <span className="min-w-0 flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rotate-45 shrink-0 ${c.source === "manual" ? "bg-white border border-red-500" : "bg-red-500"}`} />
                      <span className="truncate text-[11px] font-semibold text-slate-600">{c.name || "Tanpa nama"}</span>
                    </span>
                    <span className="shrink-0 flex items-center gap-2 text-[10px] tabular-nums">
                      {c.rating != null && <span className="inline-flex items-center gap-0.5 font-bold text-amber-600"><Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{c.rating.toFixed(1)}</span>}
                      <span className="text-slate-400">{fmtDistance(c._dist)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {error && <p className="text-[10px] text-red-500">{error}</p>}
        <button onClick={onDelete}
          className="w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-[11px] font-bold text-red-500 hover:bg-red-50">
          <Trash2 className="w-3.5 h-3.5" /> Hapus toko
        </button>
      </div>
      </div>
    </div>
  );
}

// ── Store deep-analysis report modal (same hero + sections + PDF as the live page) ─
function StoreReportModal({ store, result, score, subcategory, targetSes, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [activeVer, setActiveVer] = useState(null);   // null = latest (the passed result)
  const [shown, setShown] = useState(result);          // result currently displayed

  // Version history — free to re-view older generations.
  useEffect(() => {
    brListStoreDAVersions(store.id).then(setVersions).catch(() => setVersions([]));
  }, [store.id]);

  async function pickVersion(v) {
    if (v == null) { setActiveVer(null); setShown(result); return; }
    try {
      const data = await brGetStoreDAVersion(store.id, v);
      setActiveVer(v);
      setShown(data.result);
    } catch { /* ignore */ }
  }

  const view = deriveStoreDeepAnalysisView({ store, result: shown, score, subcategory, targetSes });

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      exportDeepAnalysisPDF({
        analysis: view.analysis, fitView: view.fitView, result: shown, profileView: view.profileView,
        yourFit: view.yourFit, score: view.score, idx: view.idx, verdict: view.verdict,
        gridLabel: view.gridLabel, fullLocation: view.fullLocation, namaKabkot: view.namaKabkot,
        subcategory: view.subcategory, target_ses: view.targetSes, radius: shown?.radius || "3km",
        resolution: view.resolution, lat: view.lat, lng: view.lng, generatedAt: Date.now(),
      });
    } catch (e) { alert(`Gagal membuat PDF: ${e.message}`); }
    finally { setDownloading(false); }
  }

  const radius = shown?.radius || "3km";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <h2 className="font-bold text-slate-800 text-sm truncate">{store.name} · Analisis Mendalam</h2>
            <p className="text-[11px] text-slate-400 truncate">{view.fullLocation}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Version history */}
            {versions.length > 1 && (
              <select value={activeVer ?? ""} onChange={(e) => pickVersion(e.target.value === "" ? null : Number(e.target.value))}
                className="rounded-xl border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 focus:outline-none">
                <option value="">Terbaru (v{versions[0]?.versionNo})</option>
                {versions.map((v) => (
                  <option key={v.versionNo} value={v.versionNo}>
                    v{v.versionNo} · {v.radius || "—"} · {v.createdAt ? new Date(v.createdAt).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : ""}
                  </option>
                ))}
              </select>
            )}
            <button onClick={() => setShareOpen(true)}
              className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
              <Share2 className="w-3.5 h-3.5" /> Bagikan
            </button>
            <ShareModal open={shareOpen} onClose={() => setShareOpen(false)}
              kind="klasik" storeId={store.id} title={`${store.name} · Analisis Mendalam`}
              ownerLabel={(() => { try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}").businessName || null; } catch { return null; } })()}
              buildPayload={() => deepAnalysisTicket({
                analysis: view.analysis, fitView: view.fitView, result: shown, profileView: view.profileView,
                yourFit: view.yourFit, score: view.score, idx: view.idx, verdict: view.verdict,
                gridLabel: view.gridLabel, fullLocation: view.fullLocation, namaKabkot: view.namaKabkot,
                subcategory: view.subcategory, target_ses: view.targetSes, radius: shown?.radius || "3km",
                resolution: view.resolution, lat: view.lat, lng: view.lng, generatedAt: Date.now(),
              })} />
            <button onClick={handleDownload} disabled={downloading}
              className="flex items-center gap-2 rounded-xl bg-brand-700 px-4 py-2 text-xs font-bold text-white hover:bg-brand-800 disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> {downloading ? "Membuat PDF…" : "Unduh PDF"}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {activeVer != null && (
          <div className="px-5 py-1.5 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-700 shrink-0">
            Melihat versi lama <b>v{activeVer}</b> (gratis) — pilih "Terbaru" untuk kembali.
          </div>
        )}
        <div className="overflow-y-auto bg-slate-100">
          <DeepAnalysisHero
            gridLabel={view.gridLabel} fullLocation={view.fullLocation} result={shown}
            resolution={view.resolution} lat={view.lat} lng={view.lng} verdict={view.verdict}
            score={view.score} idx={view.idx} yourFit={view.yourFit} subcategory={view.subcategory}
            targetSes={view.targetSes} radius={radius} />
          <div className="max-w-6xl mx-auto px-8 py-8">
            <DeepAnalysisReport result={shown} targetSes={view.targetSes} radius={radius} profileView={view.profileView}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold text-slate-700 text-right">{children}</span>
    </div>
  );
}

function LayerToggle({ on, onClick, color, label, hint, diamond, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center gap-2 rounded-lg px-2 py-1 w-full text-left transition-opacity disabled:cursor-not-allowed ${on ? "opacity-100" : "opacity-35"} ${disabled ? "opacity-30" : "hover:bg-slate-50"}`}>
      <span className={`w-2.5 h-2.5 shrink-0 border border-black/10 ${diamond ? "rotate-45" : "rounded-sm"}`} style={{ backgroundColor: on ? color : "#cbd5e1" }} />
      <span className="text-[10px] font-bold text-slate-600 whitespace-nowrap">{label}</span>
      {hint && <span className="text-[9px] text-slate-400 ml-auto whitespace-nowrap">{hint}</span>}
    </button>
  );
}

// ── Monitor result ───────────────────────────────────────────────────────────
function MonitorResultModal({ store, res, runs, onClose, onViewReport }) {
  const delta = res.demandDelta;
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const deltaColor = delta == null || delta === 0 ? "#64748b" : delta > 0 ? "#16a34a" : "#dc2626";
  const newComp = res.newCompetitors || [];
  const series = [...runs].reverse().map((r) => r.demandScore ?? 0); // oldest → newest
  const maxD = Math.max(...series, 1);

  return (
    <Modal title={`${store.name} · Pantau`} onClose={onClose} wide>
      <div className="space-y-4">
        {/* Headline metrics */}
        <div className="grid grid-cols-3 gap-2">
          <Metric label="Skor permintaan" value={res.demandScore != null ? Math.round(res.demandScore) : "—"}
            sub={delta != null ? <span className="inline-flex items-center gap-0.5" style={{ color: deltaColor }}><DeltaIcon className="w-3 h-3" />{delta > 0 ? "+" : ""}{delta.toFixed(1)} vs sebelumnya</span> : "pemindaian pertama"} />
          <Metric label="Kompetitor" value={res.competitorCount ?? 0} sub="dalam jangkauan" />
          <Metric label="Baru kali ini" value={newComp.length} sub={res.goneCompetitors ? `${res.goneCompetitors} tutup` : "tidak ada yang tutup"}
            accent={newComp.length ? "#ea4335" : undefined} />
        </div>

        {/* New competitors */}
        {newComp.length > 0 && (
          <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Kompetitor baru terdeteksi</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {newComp.map((n, i) => (
                <span key={i} className="text-[11px] font-semibold bg-white border border-amber-200 text-amber-800 rounded-lg px-2 py-1">{n}</span>
              ))}
            </div>
          </div>
        )}

        {/* Demand trend */}
        {series.length > 1 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Tren permintaan · {series.length} pemindaian terakhir</p>
            <div className="flex items-end gap-1 h-16">
              {series.map((v, i) => (
                <div key={i} className="flex-1 rounded-t" title={`${Math.round(v)}`}
                  style={{ height: `${Math.max(6, (v / maxD) * 100)}%`, backgroundColor: i === series.length - 1 ? "#12805a" : "#cbd5e1" }} />
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-slate-400 leading-relaxed">
          Kompetitor dibaca langsung dari Google Maps di sekitar toko ini. Deteksi baru/tutup membandingkan dengan pemindaian sebelumnya.
          Pemindaian manual dibatasi per hari (dapat diatur oleh admin).
        </p>
      </div>
      <div className="flex justify-between items-center mt-4">
        {onViewReport
          ? <button onClick={onViewReport} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-brand-700 hover:text-brand-800"><TrendingUp className="w-3.5 h-3.5" /> Lihat Laporan Pemantauan lengkap →</button>
          : <span />}
        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800">Selesai</button>
      </div>
    </Modal>
  );
}

function Metric({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-2xl font-extrabold mt-0.5" style={{ color: accent || "#1e293b" }}>{value}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

// ── Monitor Report (full, separate from deep analysis) ───────────────────────
// Tracks what CHANGES over time: demand, the store's OWN Google rating + reviews,
// competitor count and the competitor set's average rating. Supports trends,
// comparing any two runs, a live Google Maps embed, and PDF export.
const fmtDate = (iso, withTime) =>
  iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) }) : "—";
const fmtRating = (v) => (v == null ? "—" : Number(v).toFixed(1));
const deltaBadge = (d, opts = {}) => {
  if (d == null || d === 0) return <span className="inline-flex items-center gap-0.5 text-slate-400"><Minus className="w-3 h-3" />tidak berubah</span>;
  const good = opts.invert ? d < 0 : d > 0;
  const Icon = d > 0 ? TrendingUp : TrendingDown;
  const color = good ? "#16a34a" : "#dc2626";
  return <span className="inline-flex items-center gap-0.5" style={{ color }}><Icon className="w-3 h-3" />{d > 0 ? "+" : ""}{opts.fixed != null ? d.toFixed(opts.fixed) : d}</span>;
};

// Simple bar sparkline (oldest → newest); last bar emphasised.
function Sparkbars({ data, min = 0, max, fmt = (v) => Math.round(v), accent = "#12805a", unit }) {
  const vals = data.map((d) => (d.value == null ? null : Number(d.value)));
  const hi = max != null ? max : Math.max(1, ...vals.filter((v) => v != null));
  const lo = min;
  if (!data.length) return <p className="text-[10px] text-slate-300">Belum ada data.</p>;
  return (
    <div>
      <div className="flex items-end gap-1 h-14">
        {data.map((d, i) => {
          const v = d.value == null ? null : Number(d.value);
          const h = v == null ? 0 : Math.max(6, ((v - lo) / (hi - lo || 1)) * 100);
          const last = i === data.length - 1;
          return (
            <div key={i} className="flex-1 flex flex-col justify-end items-center" title={`${d.label}: ${v == null ? "—" : fmt(v)}${unit || ""}`}>
              {v == null
                ? <div className="w-full rounded-t border border-dashed border-slate-200" style={{ height: 6 }} />
                : <div className="w-full rounded-t" style={{ height: `${h}%`, backgroundColor: last ? accent : "#cbd5e1" }} />}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-slate-300">
        <span>{data[0]?.label}</span>
        <span className="font-bold text-slate-500">{vals[vals.length - 1] == null ? "—" : `${fmt(vals[vals.length - 1])}${unit || ""}`}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
}

function TrendCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{title}</p>
      {children}
    </div>
  );
}

function MonitorReportModal({ store, runs, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const ordered = useMemo(() => [...runs].reverse(), [runs]);   // oldest → newest
  const latest = runs[0] || null;
  const prev = runs[1] || null;
  const [aId, setAId] = useState(prev?.id ?? latest?.id ?? null);   // compare baseline
  const [bId, setBId] = useState(latest?.id ?? null);               // compare target
  const runA = runs.find((r) => r.id === aId) || null;
  const runB = runs.find((r) => r.id === bId) || null;

  const label = (r) => fmtDate(r?.runAt);
  const mapsUrl = store.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`;
  const embedUrl = `https://www.google.com/maps?q=${store.lat},${store.lng}&z=16&output=embed`;

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try { await buildMonitorReportPDF(store, runs); }
    catch (e) { alert(`Gagal membuat PDF: ${e.message}`); }
    finally { setDownloading(false); }
  }

  const compCount = (a, b) => (a != null && b != null ? b - a : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <h2 className="font-bold text-slate-800 text-sm truncate">{store.name} · Laporan Pemantauan</h2>
            <p className="text-[11px] text-slate-400 truncate">{[store.namaKelurahan, store.namaKabkot].filter(Boolean).join(", ") || `${store.lat?.toFixed?.(4)}, ${store.lng?.toFixed?.(4)}`}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleDownload} disabled={downloading || !runs.length}
              className="flex items-center gap-2 rounded-xl bg-brand-700 px-4 py-2 text-xs font-bold text-white hover:bg-brand-800 disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> {downloading ? "Membuat PDF…" : "Unduh PDF"}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 bg-slate-50/40">
          {!runs.length ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center mb-3"><Radar className="w-5 h-5 text-slate-300" /></div>
              <p className="text-xs font-bold text-slate-500">Belum ada pemindaian pemantauan</p>
              <p className="text-[10px] text-slate-400 mt-1 leading-relaxed max-w-xs">Jalankan "Pantau Sekarang" atau aktifkan jadwal pemindaian otomatis. Setiap pemindaian mencatat kompetitor, rating Google Anda, dan ulasan baru sehingga laporan ini dapat melacak trennya.</p>
            </div>
          ) : (
            <>
              {/* Latest snapshot */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Snapshot terbaru · {fmtDate(latest.runAt, true)} <span className="capitalize text-slate-300">({latest.trigger})</span></p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Metric label="Skor permintaan" value={latest.demandScore != null ? Math.round(latest.demandScore) : "—"}
                    sub={deltaBadge(latest.demandDelta, { fixed: 1 })} />
                  <Metric label="Rating Google Anda" value={fmtRating(latest.ownRating)}
                    accent={latest.ownRating != null ? "#12805a" : undefined}
                    sub={<span className="flex items-center gap-1">{deltaBadge(latest.ownRatingDelta, { fixed: 1 })}{latest.ownRatingsTotal != null && <span className="text-slate-300">· {latest.ownRatingsTotal} ulasan</span>}</span>} />
                  <Metric label="Kompetitor" value={latest.competitorCount ?? 0}
                    sub={prev ? deltaBadge(compCount(prev.competitorCount, latest.competitorCount), { invert: true }) : "pemindaian pertama"} />
                  <Metric label="Rating rata-rata rival" value={fmtRating(latest.competitorAvgRating)}
                    sub={latest.competitorAvgRating != null && latest.ownRating != null
                      ? (latest.ownRating >= latest.competitorAvgRating ? <span className="text-emerald-600">Anda unggul</span> : <span className="text-red-500">Anda tertinggal</span>)
                      : "rival aktif"} />
                </div>
                {latest.newReviews > 0 && (
                  <div className="mt-2 rounded-xl border border-brand-100 bg-teal-50/60 p-2.5 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-brand-600 shrink-0" />
                    <span className="text-[11px] text-brand-800"><b>{latest.newReviews}</b> ulasan baru pada toko Anda sejak pemindaian sebelumnya.</span>
                  </div>
                )}
                {latest.reviewTheme && (
                  <p className="mt-2 text-[11px] text-slate-500"><span className="font-bold text-slate-600">Tema ulasan:</span> {latest.reviewTheme}</p>
                )}
                {(latest.newCompetitors?.length > 0) && (
                  <div className="mt-2 rounded-xl border border-amber-100 bg-amber-50/60 p-2.5">
                    <div className="flex items-center gap-1.5 mb-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /><span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Kompetitor baru pemindaian ini</span></div>
                    <div className="flex flex-wrap gap-1.5">
                      {latest.newCompetitors.map((n, i) => <span key={i} className="text-[11px] font-semibold bg-white border border-amber-200 text-amber-800 rounded-lg px-2 py-1">{n}</span>)}
                    </div>
                  </div>
                )}
              </div>

              {/* Trends */}
              {ordered.length > 1 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Tren · {ordered.length} pemindaian terakhir</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <TrendCard title="Skor permintaan">
                      <Sparkbars data={ordered.map((r) => ({ value: r.demandScore, label: label(r) }))} max={100} />
                    </TrendCard>
                    <TrendCard title="Rating Google Anda">
                      <Sparkbars data={ordered.map((r) => ({ value: r.ownRating, label: label(r) }))} min={0} max={5} fmt={(v) => v.toFixed(1)} accent="#12805a" />
                    </TrendCard>
                    <TrendCard title="Kompetitor dalam jangkauan">
                      <Sparkbars data={ordered.map((r) => ({ value: r.competitorCount, label: label(r) }))} accent="#ea4335" />
                    </TrendCard>
                    <TrendCard title="Rating rata-rata rival">
                      <Sparkbars data={ordered.map((r) => ({ value: r.competitorAvgRating, label: label(r) }))} min={0} max={5} fmt={(v) => v.toFixed(1)} accent="#f59e0b" />
                    </TrendCard>
                  </div>
                </div>
              )}

              {/* Compare two runs */}
              {runs.length > 1 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Bandingkan pemindaian</p>
                  <div className="rounded-xl border border-slate-100 bg-white p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <select value={aId ?? ""} onChange={(e) => setAId(e.target.value)}
                        className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] font-semibold text-slate-600 focus:outline-none">
                        {runs.map((r) => <option key={r.id} value={r.id}>{fmtDate(r.runAt, true)}</option>)}
                      </select>
                      <span className="text-slate-300 text-xs">→</span>
                      <select value={bId ?? ""} onChange={(e) => setBId(e.target.value)}
                        className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] font-semibold text-slate-600 focus:outline-none">
                        {runs.map((r) => <option key={r.id} value={r.id}>{fmtDate(r.runAt, true)}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
                      <CompareRow label="Permintaan" a={runA?.demandScore} b={runB?.demandScore} fmt={(v) => Math.round(v)} />
                      <CompareRow label="Rating Anda" a={runA?.ownRating} b={runB?.ownRating} fmt={fmtRating} fixed={1} />
                      <CompareRow label="Kompetitor" a={runA?.competitorCount} b={runB?.competitorCount} fmt={(v) => v} invert />
                      <CompareRow label="Rata-rata rival" a={runA?.competitorAvgRating} b={runB?.competitorAvgRating} fmt={fmtRating} fixed={1} />
                    </div>
                  </div>
                </div>
              )}

              {/* Location (live Google Maps) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Lokasi</p>
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-600 hover:text-brand-700"><MapPin className="w-3 h-3" /> Buka di Google Maps</a>
                </div>
                <div className="rounded-xl overflow-hidden border border-slate-100">
                  <iframe title="store-location" src={embedUrl} className="w-full" style={{ height: 220, border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
              </div>

              {/* History */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Riwayat pemindaian</p>
                <div className="rounded-xl border border-slate-100 bg-white overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-100">
                        {["Tanggal", "Pemicu", "Permintaan", "Rating", "Ulasan+", "Rival", "Rata² rival"].map((h) => (
                          <th key={h} className="text-left font-bold uppercase tracking-wider text-[9px] px-3 py-2 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((r) => (
                        <tr key={r.id} className="border-b border-slate-50 last:border-0">
                          <td className="px-3 py-2 whitespace-nowrap font-semibold text-slate-600">{fmtDate(r.runAt, true)}</td>
                          <td className="px-3 py-2 capitalize text-slate-400">{r.trigger}</td>
                          <td className="px-3 py-2 tabular-nums">{r.demandScore != null ? Math.round(r.demandScore) : "—"}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtRating(r.ownRating)}</td>
                          <td className="px-3 py-2 tabular-nums">{r.newReviews || 0}</td>
                          <td className="px-3 py-2 tabular-nums">{r.competitorCount ?? "—"}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtRating(r.competitorAvgRating)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-[10px] text-slate-400 leading-relaxed">
                Rating Google dan ulasan baru Anda dibaca dari listing Google toko Anda sendiri; kompetitor dibaca langsung di sekitar toko. Ulasan baru dihitung dengan membandingkan teks ulasan terhadap pemindaian sebelumnya. Data rating/kompetitor hanya seaktual pemindaian pemantauan terakhir.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CompareRow({ label, a, b, fmt, fixed, invert }) {
  const d = a != null && b != null ? (fixed != null ? Number((b - a).toFixed(fixed)) : b - a) : null;
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
      <span className="text-sm font-extrabold text-slate-700 tabular-nums">{a == null ? "—" : fmt(a)} <span className="text-slate-300 font-normal">→</span> {b == null ? "—" : fmt(b)}</span>
      <span className="text-[10px] mt-0.5">{deltaBadge(d, { fixed, invert })}</span>
    </div>
  );
}

// Pure jsPDF monitor-report export — text + a manual history table, no html2canvas.
async function buildMonitorReportPDF(store, runs) {
  const { jsPDF } = await lazyModule(() => import("jspdf"));
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const W = 210, M = 16;
  const teal = [15, 118, 110], slate = [71, 85, 105], grey = [148, 163, 184];
  let y = 0;

  // Header band
  pdf.setFillColor(...teal); pdf.rect(0, 0, W, 26, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(9);
  pdf.text("LAPORAN PEMANTAUAN", M, 11);
  pdf.setFontSize(15); pdf.text(store.name || "Toko", M, 19);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8);
  const loc = [store.namaKelurahan, store.namaKabkot].filter(Boolean).join(", ");
  pdf.text(loc || `${store.lat}, ${store.lng}`, M, 24);
  y = 34;

  const latest = runs[0];
  if (!latest) {
    pdf.setTextColor(...slate); pdf.setFontSize(10);
    pdf.text("Belum ada pemindaian pemantauan yang tercatat.", M, y);
    pdf.save(`${(store.name || "store").replace(/\s+/g, "_")}_monitor_report.pdf`);
    return;
  }

  pdf.setTextColor(...grey); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7);
  pdf.text(`SNAPSHOT TERBARU · ${fmtDate(latest.runAt, true)} (${latest.trigger})`, M, y);
  y += 6;

  const cards = [
    ["Skor permintaan", latest.demandScore != null ? String(Math.round(latest.demandScore)) : "—", latest.demandDelta != null ? `${latest.demandDelta > 0 ? "+" : ""}${latest.demandDelta.toFixed(1)} vs sebelumnya` : ""],
    ["Rating Google Anda", fmtRating(latest.ownRating), latest.ownRatingsTotal != null ? `${latest.ownRatingsTotal} ulasan` : ""],
    ["Kompetitor", String(latest.competitorCount ?? 0), latest.newCompetitors?.length ? `${latest.newCompetitors.length} baru` : ""],
    ["Rating rata-rata rival", fmtRating(latest.competitorAvgRating), latest.newReviews ? `${latest.newReviews} ulasan baru` : ""],
  ];
  const cw = (W - 2 * M - 9) / 4;
  cards.forEach((c, i) => {
    const x = M + i * (cw + 3);
    pdf.setDrawColor(226, 232, 240); pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(x, y, cw, 20, 2, 2, "FD");
    pdf.setTextColor(...grey); pdf.setFont("helvetica", "bold"); pdf.setFontSize(6);
    pdf.text(c[0].toUpperCase(), x + 3, y + 5);
    pdf.setTextColor(...slate); pdf.setFontSize(15); pdf.text(c[1], x + 3, y + 13);
    pdf.setTextColor(...grey); pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5);
    if (c[2]) pdf.text(c[2], x + 3, y + 18);
  });
  y += 27;

  if (latest.reviewTheme) {
    pdf.setTextColor(...slate); pdf.setFont("helvetica", "bold"); pdf.setFontSize(8);
    pdf.text("Tema ulasan: ", M, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(pdf.splitTextToSize(latest.reviewTheme, W - 2 * M - 24), M + 24, y);
    y += 8;
  }

  // History table
  pdf.setTextColor(...grey); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7);
  pdf.text("RIWAYAT PEMINDAIAN", M, y); y += 5;
  const cols = [
    ["Tanggal", 34], ["Pemicu", 20], ["Permintaan", 20], ["Rating", 18], ["Ulasan+", 20], ["Rival", 18], ["Rata2 Rival", 20],
  ];
  const startX = M;
  pdf.setFillColor(241, 245, 249); pdf.rect(startX, y, W - 2 * M, 6, "F");
  pdf.setTextColor(...slate); pdf.setFontSize(6.5); pdf.setFont("helvetica", "bold");
  let cx = startX + 2;
  cols.forEach(([h, w]) => { pdf.text(h.toUpperCase(), cx, y + 4); cx += w; });
  y += 6;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(7);
  runs.forEach((r, i) => {
    if (y > 280) { pdf.addPage(); y = 20; }
    if (i % 2) { pdf.setFillColor(250, 251, 252); pdf.rect(startX, y, W - 2 * M, 6, "F"); }
    const row = [
      fmtDate(r.runAt, true), r.trigger,
      r.demandScore != null ? String(Math.round(r.demandScore)) : "—",
      fmtRating(r.ownRating), String(r.newReviews || 0),
      r.competitorCount != null ? String(r.competitorCount) : "—",
      fmtRating(r.competitorAvgRating),
    ];
    cx = startX + 2; pdf.setTextColor(...slate);
    row.forEach((cell, j) => { pdf.text(String(cell), cx, y + 4); cx += cols[j][1]; });
    y += 6;
  });

  y += 6;
  pdf.setTextColor(...grey); pdf.setFont("helvetica", "italic"); pdf.setFontSize(6.5);
  pdf.text(pdf.splitTextToSize(`Dibuat ${fmtDate(new Date().toISOString(), true)}. Data rating dan kompetitor mencerminkan pemindaian pemantauan terakhir. Ulasan baru dihitung dengan membandingkan teks ulasan antar-pemindaian.`, W - 2 * M), M, y);

  pdf.save(`${(store.name || "store").replace(/\s+/g, "_")}_monitor_report.pdf`);
}

// ── Tabular city dashboard (Phase D) ─────────────────────────────────────────
function StoreTableModal({ stores, cities, city, competitors, onClose, onEdit, onLocate }) {
  const [q, setQ] = useState("");
  const [cityFilter, setCityFilter] = useState(city && city !== "all" ? city : "all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "name", dir: 1 });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = stores.filter((s) => s.active !== false);
    if (cityFilter !== "all") list = list.filter((s) => s.kodeKabkot === cityFilter);
    if (statusFilter !== "all") list = list.filter((s) => (s.status || "running") === statusFilter);
    if (needle) list = list.filter((s) =>
      [s.name, s.storeClass, s.namaKelurahan, s.namaKecamatan, s.namaKabkot, s.notes, s.performa]
        .filter(Boolean).some((v) => v.toLowerCase().includes(needle)));
    const val = (s) => {
      switch (sort.key) {
        case "class":  return s.storeClass || "";
        case "status": return s.status || "running";
        case "area":   return s.namaKecamatan || s.namaKelurahan || "";
        case "comp":   return (competitors[s.id] || []).length;
        default:       return s.name || "";
      }
    };
    return [...list].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (typeof av === "number") return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
  }, [stores, q, cityFilter, statusFilter, sort, competitors]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const Th = ({ k, children, right }) => (
    <th onClick={k ? () => toggleSort(k) : undefined}
      className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap ${right ? "text-right" : "text-left"} ${k ? "cursor-pointer hover:text-slate-600 select-none" : ""}`}>
      {children}{k && sort.key === k ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
    </th>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="font-bold text-slate-800 text-sm">Tabular · semua toko</h2>
            <p className="text-[11px] text-slate-400">{rows.length} dari {stores.filter((s) => s.active !== false).length} toko</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100 shrink-0">
          <div className="relative flex-1 min-w-[200px]">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, wilayah, catatan…"
              className="w-full rounded-xl border border-slate-200 pl-3 pr-8 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"><X className="w-3.5 h-3.5" /></button>}
          </div>
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
            className="rounded-xl border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 focus:outline-none">
            <option value="all">Semua kota</option>
            {cities.map((c) => <option key={c.kodeKabkot} value={c.kodeKabkot}>{c.namaKabkot}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 focus:outline-none">
            <option value="all">Semua status</option>
            {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
          </select>
        </div>

        <div className="overflow-auto">
          {rows.length === 0 ? (
            <div className="py-16 text-center text-xs text-slate-400">Tidak ada toko yang cocok.</div>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-100">
                <tr>
                  <Th k="name">Toko</Th>
                  <Th k="class">Kelas</Th>
                  <Th k="status">Status</Th>
                  <Th k="area">Wilayah</Th>
                  <Th k="comp" right>Rival</Th>
                  <Th>Catatan</Th>
                  <Th>Performa</Th>
                  <Th right>Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const st = s.status || "running";
                  const nComp = competitors[s.id]?.length;
                  return (
                    <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-semibold text-slate-700 whitespace-nowrap max-w-[180px] truncate">
                        {s.name}
                        {s.googleMapsUrl && <a href={s.googleMapsUrl} target="_blank" rel="noreferrer" className="ml-1 text-brand-500 hover:text-brand-600" onClick={(e) => e.stopPropagation()}>🔗</a>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: classColor(s.storeClass) }} />{s.storeClass || "—"}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: `${STATUS_META[st].color}18`, color: STATUS_META[st].color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_META[st].color }} />{STATUS_META[st].label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap max-w-[160px] truncate">{[s.namaKelurahan, s.namaKecamatan].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{nComp != null ? nComp : "—"}</td>
                      <td className="px-3 py-2 text-slate-400 max-w-[160px] truncate" title={s.notes || ""}>{s.notes || "—"}</td>
                      <td className="px-3 py-2 text-slate-400 max-w-[140px] truncate" title={s.performa || ""}>{s.performa || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <button onClick={() => onLocate(s)} className="text-[10px] font-bold text-slate-400 hover:text-brand-600 mr-2">Tuju</button>
                        <button onClick={() => onEdit(s)} className="text-[10px] font-bold text-brand-600 hover:text-brand-700">Ubah</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <p className="px-5 py-2 text-[10px] text-slate-400 border-t border-slate-100 shrink-0">Jumlah rival hanya tampil untuk toko yang kompetitornya sudah dimuat (buka sebuah toko atau "Tampilkan semua kompetitor" di peta).</p>
      </div>
    </div>
  );
}

function StoreEditModal({ store, classes, onClose, onSave }) {
  const [form, setForm] = useState({
    name: store.name || "", storeClass: store.storeClass || "", status: store.status || "running",
    lat: store.lat ?? "", lng: store.lng ?? "", externalRef: store.externalRef || "",
    googleMapsUrl: store.googleMapsUrl || "", notes: store.notes || "", performa: store.performa || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.name.trim()) { setErr("Nama wajib diisi."); return; }
    const lat = Number(form.lat), lng = Number(form.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setErr("Latitude/longitude harus berupa koordinat yang valid."); return;
    }
    setBusy(true); setErr(null);
    try {
      // lat/lng changes make the backend re-resolve the store's region (kelurahan/kabkot).
      await onSave({
        name: form.name.trim(),
        storeClass: form.storeClass || null,
        status: form.status,
        lat, lng,
        externalRef: form.externalRef.trim(),
        googleMapsUrl: form.googleMapsUrl.trim(),
        notes: form.notes.trim(),
        performa: form.performa.trim(),
      });
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  const knownClasses = classes.map((c) => c.classLabel).filter(Boolean);
  const classOptions = form.storeClass && !knownClasses.includes(form.storeClass)
    ? [form.storeClass, ...knownClasses] : knownClasses;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 text-sm">Ubah toko</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <Field label="Nama">
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kelas">
              <select value={form.storeClass} onChange={(e) => set("storeClass", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 focus:outline-none">
                <option value="">Belum dikelaskan</option>
                {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 focus:outline-none">
                {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude">
              <input type="number" step="any" value={form.lat} onChange={(e) => set("lat", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400" />
            </Field>
            <Field label="Longitude">
              <input type="number" step="any" value={form.lng} onChange={(e) => set("lng", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400" />
            </Field>
          </div>
          <Field label="Referensi (ID eksternal)">
            <input value={form.externalRef} onChange={(e) => set("externalRef", e.target.value)} placeholder="opsional"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400" />
          </Field>
          <Field label="URL Google Maps">
            <input value={form.googleMapsUrl} onChange={(e) => set("googleMapsUrl", e.target.value)} placeholder="https://maps.app.goo.gl/…"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400" />
          </Field>
          <Field label="Catatan">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400 resize-none" />
          </Field>
          <Field label="Catatan performa">
            <textarea value={form.performa} onChange={(e) => set("performa", e.target.value)} rows={2}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-brand-400 resize-none" />
          </Field>
          {err && <p className="text-[11px] text-red-500">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-50">Batal</button>
          <button onClick={submit} disabled={busy}
            className="px-4 py-2 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800 disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Simpan perubahan
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyStores({ onUpload }) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-10 text-center">
      <div className="w-11 h-11 rounded-2xl bg-slate-50 flex items-center justify-center mb-3"><StoreIcon className="w-5 h-5 text-slate-300" /></div>
      <p className="text-xs font-bold text-slate-500">Belum ada toko</p>
      <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">Unggah CSV cabang Anda (nama, kelas, latitude, longitude) atau tambahkan satu di peta.</p>
      <button onClick={onUpload} className="mt-3 text-[11px] font-bold text-brand-600 hover:text-brand-700">Unggah CSV →</button>
    </div>
  );
}

function Legend({ loading }) {
  return (
    <div className="absolute bottom-4 right-4 z-10 flex gap-3 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2">
      <div>
        <div className="flex items-center gap-1 mb-1.5">
          <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Permintaan</span>
          {loading && <Loader2 className="w-3 h-3 text-slate-300 animate-spin" />}
        </div>
        <div className="flex flex-col gap-1">
          {[["Utama (jangkauan Anda)", "#34a853"], ["Belum tergarap", "#a855f7"], ["Terbagi / diperebutkan", "#ea4335"]].map(([l, c]) => (
            <div key={l} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
              <span className="text-[10px] text-slate-500">{l}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="border-l border-slate-100 pl-3">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Toko</p>
        <div className="flex flex-col gap-1">
          {STATUS_KEYS.map((k) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_META[k].color }} />
              <span className="text-[10px] text-slate-500">{STATUS_META[k].label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="w-2.5 h-2.5 rotate-45 bg-red-500" />
            <span className="text-[10px] text-slate-500">Kompetitor</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Upload CSV ───────────────────────────────────────────────────────────────
function UploadModal({ onClose, onDone }) {
  const [parsed, setParsed] = useState(null);   // {rows, errors}
  const [fileName, setFileName] = useState("");
  const [crawl, setCrawl] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setParsed(parseStoreCsv(String(reader.result)));
    reader.readAsText(f);
  }

  async function importRows() {
    if (!parsed?.rows.length) return;
    setBusy(true);
    try {
      const res = await brCreateStoresBulk(parsed.rows, {
        crawlCompetitors: crawl,
        businessType: loadCompanyProfile()?.bizSubtype || null,
      });
      setResult(res);
    } catch (e) { setResult({ error: e.message }); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="Unggah toko (CSV)" onClose={onClose}>
      {!result ? (
        <div className="space-y-3">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Wajib: <b>name, class, latitude, longitude</b> (berdasarkan urutan kolom atau header).
            Opsional (dengan baris header): <b>status, notes, google_maps_url, performa</b>.
            Status kosong/tidak dikenal → <b>planning</b>.
          </p>
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 overflow-x-auto">
            <code className="text-[10px] text-slate-500 whitespace-nowrap">
              name,class,latitude,longitude,status,notes,google_maps_url,performa
            </code>
          </div>
          <button type="button"
            onClick={() => {
              const csv = "name,class,latitude,longitude,status,notes,google_maps_url,performa\n"
                + "Toko Menteng,Flagship,-6.1958,106.8322,running,Cabang utama,https://share.google/xxxx,Ramai akhir pekan\n"
                + "Toko Dramaga,Satellite,-6.5583,106.7311,planning,,,";
              const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
              const a = document.createElement("a"); a.href = url; a.download = "branch-radar-template.csv"; a.click();
              URL.revokeObjectURL(url);
            }}
            className="text-[11px] font-semibold text-brand-600 hover:underline">
            Unduh templat CSV
          </button>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-6 cursor-pointer hover:border-brand-300 text-slate-500">
            <FileText className="w-4 h-4" />
            <span className="text-xs font-semibold">{fileName || "Pilih file .csv"}</span>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          </label>
          {parsed && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-bold text-slate-700">{parsed.rows.length} baris valid{parsed.errors.length ? ` · ${parsed.errors.length} dilewati` : ""}</p>
              {parsed.rows.slice(0, 3).map((r, i) => (
                <p key={i} className="text-[10px] text-slate-500 mt-1">
                  {r.name} · {r.storeClass || "—"} · {r.status}{r.googleMapsUrl ? " · 🔗" : ""} · {r.lat}, {r.lng}
                </p>
              ))}
              {parsed.errors.slice(0, 3).map((e, i) => <p key={i} className="text-[10px] text-amber-600 mt-1">{e}</p>)}
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={crawl} onChange={(e) => setCrawl(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-brand-600" />
            <span className="text-[11px] text-slate-500 leading-relaxed">
              Juga pindai kompetitor untuk setiap toko sekarang <b className="text-slate-600">(menggunakan Google Places — satu panggilan berbayar per toko)</b>. Nonaktif secara default untuk unggahan besar.
            </span>
          </label>
        </div>
      ) : result.error ? (
        <p className="text-sm text-red-500">{result.error}</p>
      ) : (
        <div className="text-center py-4">
          <div className="w-11 h-11 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-3"><StoreIcon className="w-5 h-5 text-green-600" /></div>
          <p className="text-sm font-bold text-slate-700">{result.created_count} toko diimpor</p>
          {result.skipped?.length > 0 && <p className="text-[11px] text-amber-600 mt-1">{result.skipped.length} baris dilewati</p>}
        </div>
      )}
      {!result
        ? <ModalActions onCancel={onClose} onConfirm={importRows} busy={busy} disabled={!parsed?.rows.length} confirmLabel={`Impor ${parsed?.rows.length || 0} toko`} />
        : <div className="flex justify-end mt-4"><button onClick={onDone} className="px-4 py-2 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800">Selesai</button></div>}
    </Modal>
  );
}

// ── Class settings ───────────────────────────────────────────────────────────
function ClassSettingsModal({ classes, onClose, onSaved }) {
  const [rows, setRows] = useState(() => classes.map((c) => ({ ...c })));
  const [savingLabel, setSavingLabel] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [newRadius, setNewRadius] = useState("");
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState(null);

  useEffect(() => { setRows(classes.map((c) => ({ ...c }))); }, [classes]);

  function edit(label, field, val) {
    setRows((rs) => rs.map((r) => r.classLabel === label ? { ...r, [field]: val } : r));
  }
  async function saveRow(r) {
    setSavingLabel(r.classLabel);
    try {
      await brSaveClass({ classLabel: r.classLabel, radiusKm: r.radiusKm === "" ? null : Number(r.radiusKm) });
      await onSaved();
    } finally { setSavingLabel(null); }
  }
  async function addClass() {
    const label = newLabel.trim();
    if (!label) { setAddErr("Masukkan nama kelas."); return; }
    if (rows.some((r) => r.classLabel.toLowerCase() === label.toLowerCase())) { setAddErr("Kelas itu sudah ada."); return; }
    setAdding(true); setAddErr(null);
    try {
      await brSaveClass({ classLabel: label, radiusKm: newRadius === "" ? null : Number(newRadius) });
      setNewLabel(""); setNewRadius("");
      await onSaved();
    } catch (e) { setAddErr(e.message); }
    finally { setAdding(false); }
  }

  return (
    <Modal title="Kelas toko" onClose={onClose} wide>
      <p className="text-[11px] text-slate-500 mb-3">Atur radius layanan untuk setiap kelas toko Anda — ini menentukan cincin permintaan di peta.</p>
      <div className="space-y-2">
        {rows.length > 0 && (
          <div className="grid grid-cols-[1fr_100px_64px] gap-2 text-[9px] font-bold uppercase tracking-widest text-slate-400 px-1">
            <span>Kelas</span><span>Radius (km)</span><span></span>
          </div>
        )}
        {rows.map((r) => (
          <div key={r.classLabel} className="grid grid-cols-[1fr_100px_64px] gap-2 items-center">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: classColor(r.classLabel) }} />
              <span className="text-xs font-semibold text-slate-700 truncate">{r.classLabel}</span>
              <span className="text-[10px] text-slate-400 shrink-0">×{r.storeCount ?? 0}</span>
            </div>
            <input type="number" step="0.1" value={r.radiusKm ?? ""} onChange={(e) => edit(r.classLabel, "radiusKm", e.target.value)} className="br-input !py-1.5 text-xs" placeholder="2.0" />
            <button onClick={() => saveRow(r)} disabled={savingLabel === r.classLabel}
              className="rounded-lg bg-brand-700 text-white text-[11px] font-bold py-1.5 hover:bg-brand-800 disabled:opacity-50">
              {savingLabel === r.classLabel ? "…" : "Simpan"}
            </button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-xs text-slate-400 py-3 text-center">Belum ada kelas — tambahkan yang pertama di bawah.</p>}
      </div>

      {/* Add a new class */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 px-1">Tambah kelas</p>
        <div className="grid grid-cols-[1fr_100px_64px] gap-2 items-center">
          <input value={newLabel} onChange={(e) => { setNewLabel(e.target.value); if (addErr) setAddErr(null); }}
            onKeyDown={(e) => e.key === "Enter" && addClass()} placeholder="misalnya Flagship" className="br-input !py-1.5 text-xs" />
          <input type="number" step="0.1" value={newRadius} onChange={(e) => setNewRadius(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addClass()} placeholder="2.0" className="br-input !py-1.5 text-xs" />
          <button onClick={addClass} disabled={adding}
            className="rounded-lg border border-brand-600 text-brand-700 text-[11px] font-bold py-1.5 hover:bg-teal-50 disabled:opacity-50 inline-flex items-center justify-center gap-1">
            {adding ? "…" : <><Plus className="w-3 h-3" /> Tambah</>}
          </button>
        </div>
        {addErr && <p className="text-[10px] text-red-500 mt-1.5 px-1">{addErr}</p>}
      </div>

      <div className="flex justify-end mt-4"><button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50">Tutup</button></div>
    </Modal>
  );
}

// ── Credit-safety: confirmation + full-page processing overlay ────────────────
function ConfirmDialog({ title, body, confirmLabel = "Lanjutkan", onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4.5 h-4.5 text-amber-500" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800 text-sm">{title}</h2>
            <p className="text-[11px] leading-relaxed text-slate-500 mt-1">{body}</p>
            <p className="text-[10px] font-semibold text-amber-600 mt-1.5">Ini menggunakan kredit Anda — mohon konfirmasi.</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50">Batal</button>
          <button onClick={onConfirm} className="px-4 py-2 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// Blocks the whole page while a paid action runs, so nothing can be mis-clicked.
function ProcessingOverlay({ label }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white/70 backdrop-blur-sm cursor-wait">
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-white shadow-xl border border-slate-100 px-8 py-6">
        <Loader2 className="w-7 h-7 text-brand-600 animate-spin" />
        <p className="text-sm font-semibold text-slate-700">{label}</p>
        <p className="text-[11px] text-slate-400">Mohon tunggu — jangan tutup tab ini.</p>
      </div>
    </div>
  );
}

// ── Small modal primitives ───────────────────────────────────────────────────
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-lg" : "max-w-sm"} flex flex-col max-h-[85vh]`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h2 className="font-bold text-slate-800 text-sm">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({ onCancel, onConfirm, busy, disabled, confirmLabel }) {
  return (
    <div className="flex gap-2 mt-4">
      <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50">Batal</button>
      <button onClick={onConfirm} disabled={busy || disabled}
        className="flex-1 py-2.5 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800 disabled:opacity-50">
        {busy ? "Menyimpan…" : confirmLabel}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
